import type { PRICING_COMMISSION_SERVICE_CATEGORY_SCOPES } from './adjustments';
import type {
  BudgetCategory,
  BudgetMappedItemAuditRow,
  BudgetOutput,
  CalculatedMoneyFields,
  CurrencyCode,
  ExchangeRateRecord,
  GroupPriceStructure,
  GroupThresholdInput,
  InclusionPolicyResult,
  MoneyAmount,
  NormalizedItemFlags,
  NormalizedItemInput,
  NormalizedPriceComponent,
  NormalizedPriceStructure,
  PersistenceSafeCalculatedFields,
  PersistenceSafeCalculatedItem,
  PersistenceSafeCalculatedStructure,
  PricingAdjustmentBases,
  PricingAdjustmentBlockTotals,
  PricingAdjustmentDistribution,
  PricingAdjustmentLineOutput,
  PricingAdjustmentsInput,
  PricingAdjustmentsOutput,
  PricingAgencyCommissionLineInput,
  PricingAgencyCommissionLineOutput,
  PricingDiagnostic,
  PricingEditIntent,
  PricingInclusionPolicies,
  PricingInput,
  PricingOutput,
  PricingSurchargeLineInput,
  PricingSurchargeLineOutput,
  PricingTableOutput,
  PricingTableRow,
  PricingTableRowDisplay,
  PricingTableSection,
  PricingTableTotals,
  RoomPriceInput,
  RoomPricePeriod,
  SinglePriceUnit,
  TravellerInput,
  TravellerPriceInput,
  TravellersPriceStructure,
  VatRate,
} from './types';
import currency from 'currency.js';
import { allocateByWeight } from './adjustment-allocation';
import {
  createEmptyPricingAdjustmentsOutput,
  isPricingAdjustmentPercentInRange,
  PRICING_ADJUSTMENT_BLOCK_LABELS,
} from './adjustments';
import { commissionCategoryOfContent } from './commission-rates';
import {
  isPricingAccommodationCategory,
  mapPricingContentCategoryToBudgetCategory,
  resolvePricingLegacyCategoryKey,
  resolvePricingLegacySubcategoryKey,
  toPricingLegacyCategoryLabel,
  toPricingLegacySubcategoryLabel,
} from './content-categories';
import { pricingInputSchema } from './schemas';

type MutablePricingInput = PricingInput;

type MoneyCalculation = {
  money: CalculatedMoneyFields;
  commissionAmount: number;
  finalCommissionAmount: number;
  finalCost: number;
  sourceCurrency: CurrencyCode;
  rate: number;
};

type RoomPeriodCalculation = {
  period: RoomPricePeriod;
  periodNightCount: number;
  calculation: MoneyCalculation;
};

type StructureTotal = {
  money: CalculatedMoneyFields;
  commissionAmount: number;
  finalCommissionAmount: number;
  finalCost: number;
  quantity: number;
  structure: PersistenceSafeCalculatedStructure;
  roomIds?: string[];
  travellerIds?: string[];
  thresholdIds?: string[];
};

type ItemTotal = {
  item: NormalizedItemInput;
  inclusion: InclusionPolicyResult;
  money: CalculatedMoneyFields;
  commissionAmount: number;
  finalCommissionAmount: number;
  finalCost: number;
  quantity: number;
  structure: PersistenceSafeCalculatedStructure;
  roomIds?: string[];
  travellerIds?: string[];
  thresholdIds?: string[];
};

type SetTravellerQuantityIntent = Extract<
  PricingEditIntent,
  { type: 'setTravellerQuantity' }
>;

type AddTravellerLineIntent = Extract<
  PricingEditIntent,
  { type: 'addTravellerLine' }
>;

type RemoveTravellerLineIntent = Extract<
  PricingEditIntent,
  { type: 'removeTravellerLine' }
>;

type AddRoomIntent = Extract<PricingEditIntent, { type: 'addRoom' }>;

type SetRoomUnitIntent = Extract<PricingEditIntent, { type: 'setRoomUnit' }>;

type SetItemPricingTypeIntent = Extract<
  PricingEditIntent,
  { type: 'setItemPricingType' }
>;

type AddRoomPeriodIntent = Extract<
  PricingEditIntent,
  { type: 'addRoomPeriod' }
>;

type EditIntentRoomPeriodPayload =
  | AddRoomIntent['room']['pricePeriods'][number]
  | AddRoomPeriodIntent['period'];

type TotalsAccumulator = {
  cost: number;
  netCost: number;
  finalPrice: number;
  finalPriceWithTax: number;
  profit: number;
  commission: number;
};

type PricingTableSectionDraft = Omit<
  PricingTableSection,
  'displayOrder' | 'totals'
> & {
  itemTotals: ItemTotal[];
  sortOrder: number;
};

type GroupTravellerCounts = {
  adult: number;
  child: number;
  total: number;
  byTravellerId: Map<string, number>;
};

const ZERO_VAT: VatRate = { id: null, percent: 0 };
const DEFAULT_CURRENCY: CurrencyCode = 'EUR';
const DEFAULT_PRICING_SECTION_KEY = 'OTHER';
const DEFAULT_PRICING_SECTION_LABEL = 'Other';
const PRICING_SECTION_ORDER_BY_BUDGET_CATEGORY: Record<BudgetCategory, number> =
  {
    accommodation: 0,
    activities: 1,
    dining: 2,
    logistic: 3,
    other: 4,
  };

export function calculatePricing(input: PricingInput): PricingOutput {
  const parsed = pricingInputSchema.safeParse(input);
  if (!parsed.success) {
    const baseCurrency = getInputCurrency(input);
    return createEmptyOutput(baseCurrency, [
      createDiagnostic({
        category: 'hard_validation_error',
        severity: 'error',
        code: 'PRICING_INPUT_INVALID',
        message: parsed.error.message,
        blocksPersistence: true,
      }),
    ]);
  }

  const diagnostics: PricingDiagnostic[] = [];
  const workingInput = clonePricingInput(parsed.data);
  // Legacy parity + corruption defense: a `rooms` structure must never be
  // empty. Stored `price.rooms = []` (or any path that empties it) would
  // otherwise produce a zero-room accommodation row, which the table renders
  // disabled (`canEditRow` gates on rooms.length > 0). Inject one editable
  // default room BEFORE intents so scalar edits on a previously-empty room
  // land on a real component.
  ensureNonEmptyRoomStructure(workingInput);
  // Legacy parity: NIGHT rooms are period-based (legacy always carried a
  // nightCount). A NIGHT room with no explicit periods is normalized to one
  // period spanning the whole stay so the price-periods column is editable on
  // load — not only after a Per unit -> Per night round-trip. Total is
  // unchanged: flat NIGHT already multiplies by nights (multiplier = nights),
  // and the single period uses the same per-night price x nightCount.
  ensureNightRoomsHavePeriods(workingInput);
  // Legacy parity: rows show per-category traveller lines (item-level
  // `item.travellers`). Seed the structure traveller set from the itinerary
  // travellers when absent so existing/merged data renders as editable
  // per-category rows. Applies to accommodation and scalar items alike.
  ensureItemTravellers(workingInput);
  applyEditIntents(workingInput, diagnostics);
  // Re-normalize AFTER intents: a unit-type change (e.g. Per person -> Per unit)
  // rebuilds the price structure during intent application, so the new
  // structure would otherwise have no rooms/periods/travellers and render as a
  // merged "Adults, Children" row. All three passes are idempotent.
  ensureNonEmptyRoomStructure(workingInput);
  ensureNightRoomsHavePeriods(workingInput);
  ensureItemTravellers(workingInput);
  // Legacy parity: an optional parent makes its subitems optional too, so they
  // drop out of the budget / totals / version price / export with it.
  propagateOptionalToChildren(workingInput);

  const itemTotals = workingInput.items.map((item) =>
    calculateItemTotal(item, workingInput, diagnostics)
  );

  // Collected before the table so its mapping diagnostics reach the rows;
  // finalised after, once the Surcharge / Agency Commission split exists.
  const budgetTotals = collectBudgetTotals(
    workingInput,
    itemTotals,
    diagnostics
  );
  const pricingTable = buildPricingTableOutput(
    workingInput,
    itemTotals,
    diagnostics
  );
  const budget = finalizeBudgetOutput(
    budgetTotals,
    pricingTable.adjustments.distribution,
    workingInput.context.baseCurrency
  );

  return {
    pricingTable,
    budget,
    diagnostics,
  };
}

function clonePricingInput(input: PricingInput): MutablePricingInput {
  return JSON.parse(JSON.stringify(input)) as MutablePricingInput;
}

// Legacy parity: marking a parent item optional cascades to its subitems. A
// child of an optional ancestor is itself optional, so the inclusion policies
// exclude it from the budget, pricing-table total, version price, and export
// alongside its parent (optional rows still display). Mutates `flags.isOptional`
// in place; idempotent.
function propagateOptionalToChildren(input: MutablePricingInput): void {
  const itemsById = new Map(input.items.map((item) => [item.id, item]));
  const hasOptionalAncestor = (item: NormalizedItemInput): boolean => {
    const seen = new Set<string>();
    let parentId = item.parentId;
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      const parent = itemsById.get(parentId);
      if (!parent) return false;
      if (parent.flags.isOptional) return true;
      parentId = parent.parentId;
    }
    return false;
  };
  for (const item of input.items) {
    if (!item.flags.isOptional && hasOptionalAncestor(item)) {
      item.flags = { ...item.flags, isOptional: true };
    }
  }
}

function getInputCurrency(input: PricingInput): CurrencyCode {
  return input?.context?.baseCurrency ?? DEFAULT_CURRENCY;
}

function createEmptyOutput(
  baseCurrency: CurrencyCode,
  diagnostics: PricingDiagnostic[]
): PricingOutput {
  const zero = moneyAmount(0, baseCurrency);
  const category = (name: BudgetCategory) => ({
    category: name,
    total: zero,
    originalTotal: zero,
    redistributedAmount: zero,
    percentage: 0,
    itemIds: [],
  });

  return {
    pricingTable: {
      baseCurrency,
      rows: [],
      sections: [],
      adjustments: createEmptyPricingAdjustmentsOutput(),
      totals: {
        pricingTableTotal: emptyTotals(baseCurrency),
        versionPrice: emptyTotals(baseCurrency),
        export: emptyTotals(baseCurrency),
      },
      persistenceSafe: {
        baseCurrency,
        versionPrice: zero,
        items: [],
        exchangeRateSnapshot: [],
      },
    },
    budget: {
      baseCurrency,
      originalCategoryTotals: {
        accommodation: zero,
        activities: zero,
        dining: zero,
        logistic: zero,
        other: zero,
      },
      redistribution: {
        strategy: 'none',
        sourceCategory: null,
        redistributedAmounts: {
          accommodation: zero,
          activities: zero,
          dining: zero,
          logistic: zero,
          other: zero,
        },
        reason: null,
      },
      auditRows: [],
      categories: {
        accommodation: category('accommodation'),
        activities: category('activities'),
        dining: category('dining'),
        logistic: category('logistic'),
        other: category('other'),
      },
      total: zero,
    },
    diagnostics,
  };
}

function applyEditIntents(
  input: MutablePricingInput,
  diagnostics: PricingDiagnostic[]
): void {
  for (const intent of input.editIntents) {
    const item = input.items.find(
      (candidate) => candidate.id === intent.target.itemId
    );
    if (!item) {
      diagnostics.push(
        createDiagnostic({
          category: 'mapping_warning',
          severity: 'warning',
          code: 'EDIT_TARGET_ITEM_NOT_FOUND',
          message: `Edit intent target item ${intent.target.itemId} was not found.`,
          intentId: intent.intentId,
          itemId: intent.target.itemId,
          blocksPersistence: false,
        })
      );
      continue;
    }

    applyEditIntentToItem(input, item, intent, diagnostics);
  }
}

function applyEditIntentToItem(
  input: MutablePricingInput,
  item: NormalizedItemInput,
  intent: PricingEditIntent,
  diagnostics: PricingDiagnostic[]
): void {
  // A GROUP must always carry an editable flat cost so item-level money edits
  // (cost / VAT / commission / currency / Net⇄Gross) have a component to land
  // on — older inputs or drafts may not include one.
  if (item.priceStructure.kind === 'group' && !item.priceStructure.cost) {
    // Tier-less rows keep pricing from the pre-migration scalar; a row WITH
    // tiers must never let the scalar participate, so it gets a clean price
    // line and derives from the bands.
    item.priceStructure.cost =
      (item.priceStructure.thresholds.length === 0
        ? item.priceStructure.fallbackPolicy.fallbackPrice
        : null) ??
      buildGroupPriceLineComponent(
        item.priceStructure.thresholds[0]?.adultPrice?.currency ??
          input.context.baseCurrency
      );
  }

  if (intent.type === 'addRoom') {
    applyAddRoomIntent(item, intent);
    return;
  }

  if (intent.type === 'deleteRoom') {
    applyDeleteRoomIntent(item, intent);
    return;
  }

  if (intent.type === 'addRoomPeriod') {
    applyAddRoomPeriodIntent(item, intent);
    return;
  }

  if (intent.type === 'deleteRoomPeriod') {
    applyDeleteRoomPeriodIntent(item, intent);
    return;
  }

  if (intent.type === 'addTravellerLine') {
    applyAddTravellerLineIntent(input, item, intent);
    return;
  }

  if (intent.type === 'removeTravellerLine') {
    applyRemoveTravellerLineIntent(item, intent);
    return;
  }

  if (intent.type === 'setGroupThresholds') {
    if (item.priceStructure.kind === 'group') {
      // Legacy `ItemPriceRow.onGroupPriceThresholdsChange` rebuilds the price
      // line from the new tier total (`createGroupPriceLineWithTotal`), keeping
      // only the markup. Clearing the explicit price drivers makes the new cost
      // basis drive the price again — otherwise a stored/typed price would
      // outrank it and the tier edit would appear to do nothing.
      clearGroupPriceDrivers(item.priceStructure);
      item.priceStructure.thresholds = intent.thresholds.map((threshold) => ({
        id: threshold.id,
        travellerId: threshold.travellerId ?? null,
        additionalTravellerId: threshold.additionalTravellerId ?? null,
        minTravellers: threshold.minTravellers,
        maxTravellers: threshold.maxTravellers,
        adultPrice: threshold.adultPrice,
        childPrice: threshold.childPrice,
        additionalAdultPrice: threshold.additionalAdultPrice,
        additionalChildPrice: threshold.additionalChildPrice,
      }));
    }
    return;
  }

  if (intent.type === 'setNightPeriod') {
    if (item.priceStructure.kind === 'rooms') {
      for (const room of item.priceStructure.rooms) {
        if (intent.target.roomId && room.id !== intent.target.roomId) continue;
        room.pricingMode = 'periods';
        room.pricePeriods = intent.periods
          .filter((period) => period.roomId === room.id)
          .map((period) => ({
            id: period.id,
            startDate: period.startDate,
            endDate: period.endDate,
            startNight: period.startNight,
            nightCount: period.nightCount,
            price: period.price,
          }));
      }
    }
    return;
  }

  if (intent.type === 'setRoomTypeId') {
    if (item.priceStructure.kind === 'rooms' && intent.target.roomId) {
      for (const room of item.priceStructure.rooms) {
        if (room.id !== intent.target.roomId) continue;
        room.roomTypeId = intent.roomTypeId;
        room.label = intent.roomTypeLabel ?? room.label;
      }
    }
    return;
  }

  if (intent.type === 'setRoomUnit') {
    applySetRoomUnitIntent(input, item, intent, diagnostics);
    return;
  }

  if (intent.type === 'setSingleStructureUnit') {
    if (item.priceStructure.kind === 'single') {
      item.priceStructure.unit = intent.unit;
    } else {
      diagnostics.push(
        createDiagnostic({
          category: 'mapping_warning',
          severity: 'warning',
          code: 'SINGLE_STRUCTURE_UNIT_TARGET_MISMATCH',
          message:
            'setSingleStructureUnit applied to non-single structure was ignored.',
          intentId: intent.intentId,
          itemId: item.id,
          blocksPersistence: false,
        })
      );
    }
    return;
  }

  if (intent.type === 'setItemPricingType') {
    applySetItemPricingTypeIntent(input, item, intent);
    return;
  }

  if (intent.type === 'setRoomTravellerDistribution') {
    // Money-neutral metadata replacement (legacy room.travellers).
    if (item.priceStructure.kind === 'rooms' && intent.target.roomId) {
      for (const room of item.priceStructure.rooms) {
        if (room.id !== intent.target.roomId) continue;
        room.travellers = intent.travellers.map((traveller) => ({
          travellerId: traveller.travellerId,
          quantity: traveller.quantity,
          label:
            traveller.label ??
            input.travellers.find(
              (candidate) => candidate.id === traveller.travellerId
            )?.label ??
            null,
        }));
      }
    }
    return;
  }

  if (intent.type === 'setCurrency' && item.priceStructure.kind === 'group') {
    // A GROUP's selling price lives in its threshold components, not the flat
    // cost — `forEachTargetComponent` only visits the flat cost, so a row-level
    // currency change would never reach the tiers (and the displayed currency,
    // derived from the tier components, would not change either). Restamp EVERY
    // money component (tiers + flat cost + migration fallback) so the engine
    // reprices them through the new currency's rate, exactly as single/rooms
    // rows do. Mirrors `applyComponentEdit`'s `setCurrency`: only `.currency`
    // changes; the stored amount is untouched and conversion happens later in
    // `calculateMoney`. The persisted item currency follows the tiers
    // (`resolvePersistenceCurrency`), so this round-trips on save/reload.
    const restampCurrency = (
      component: NormalizedPriceComponent | null | undefined
    ) => {
      if (component) component.currency = intent.currency;
    };
    for (const threshold of item.priceStructure.thresholds) {
      restampCurrency(threshold.adultPrice);
      restampCurrency(threshold.childPrice);
      restampCurrency(threshold.additionalAdultPrice);
      restampCurrency(threshold.additionalChildPrice);
    }
    restampCurrency(item.priceStructure.cost);
    restampCurrency(item.priceStructure.fallbackPolicy.fallbackPrice);
    return;
  }

  forEachTargetComponent(item.priceStructure, intent, (component) => {
    const rate = lookupExchangeRate(
      component.currency,
      input.context.baseCurrency,
      input
    );
    applyComponentEdit(component, intent, rate);
  });

  if (intent.type === 'setTravellerQuantity') {
    applyTravellerQuantityIntent(input, item, intent);
  }

  if (intent.type === 'setUnits') {
    if (item.priceStructure.kind === 'rooms') {
      for (const room of item.priceStructure.rooms) {
        if (intent.target.roomId && room.id !== intent.target.roomId) continue;
        room.quantity = intent.value;
      }
    }
  }
}

function applyAddRoomIntent(
  item: NormalizedItemInput,
  intent: AddRoomIntent
): void {
  if (item.priceStructure.kind !== 'rooms') return;

  const existingRoom = item.priceStructure.rooms.find(
    (room) => room.id === intent.room.id
  );
  if (existingRoom) return;

  item.priceStructure.rooms.push(mapEditIntentRoomToInput(intent));
  if (item.priceStructure.rooms.length === 1) {
    item.priceStructure.unit = intent.room.unit;
  }
}

function applyDeleteRoomIntent(
  item: NormalizedItemInput,
  intent: PricingEditIntent
): void {
  if (item.priceStructure.kind !== 'rooms' || !intent.target.roomId) return;

  const remaining = item.priceStructure.rooms.filter(
    (room) => room.id !== intent.target.roomId
  );
  // Never delete the last room — an empty rooms structure disables the whole
  // accommodation row. Legacy keeps at least one room; the UI also blocks this
  // (roomCount <= 1), this is the engine-level backstop for drafts/direct
  // intents.
  if (remaining.length === 0) return;
  item.priceStructure.rooms = remaining;
}

function applyAddRoomPeriodIntent(
  item: NormalizedItemInput,
  intent: AddRoomPeriodIntent
): void {
  if (item.priceStructure.kind !== 'rooms') return;

  const room = item.priceStructure.rooms.find(
    (candidate) => candidate.id === intent.target.roomId
  );
  if (!room) return;

  const existingPeriod = room.pricePeriods.find(
    (period) => period.id === intent.period.id
  );
  if (existingPeriod) return;

  room.pricingMode = 'periods';
  room.pricePeriods.push(mapEditIntentPeriodToInput(intent.period));
}

function applyDeleteRoomPeriodIntent(
  item: NormalizedItemInput,
  intent: PricingEditIntent
): void {
  if (
    item.priceStructure.kind !== 'rooms' ||
    !intent.target.roomId ||
    !intent.target.periodId
  ) {
    return;
  }

  for (const room of item.priceStructure.rooms) {
    if (room.id !== intent.target.roomId) continue;
    room.pricePeriods = room.pricePeriods.filter(
      (period) => period.id !== intent.target.periodId
    );
  }
}

function applySetRoomUnitIntent(
  input: MutablePricingInput,
  item: NormalizedItemInput,
  intent: SetRoomUnitIntent,
  diagnostics: PricingDiagnostic[]
): void {
  if (item.priceStructure.kind !== 'rooms' || !intent.target.roomId) return;

  const room = item.priceStructure.rooms.find(
    (candidate) => candidate.id === intent.target.roomId
  );
  if (!room || room.unit === intent.unit) return;

  if (intent.unit === 'NIGHT') {
    const nights = Math.max(resolveRoomNights(room, item), 1);
    const periodId = intent.periodId ?? `${room.id}-period-1`;
    room.unit = 'NIGHT';
    room.pricingMode = 'periods';
    room.pricePeriods = [
      {
        id: periodId,
        sourceId: null,
        startDate: null,
        endDate: null,
        startNight: 1,
        nightCount: nights,
        price: dividePriceComponent(room.price, nights),
      },
    ];
    item.priceStructure.unit = 'NIGHT';
    return;
  }

  const roomTotal = calculateRoomTotal(room, item, input, diagnostics);
  room.unit = intent.unit;
  room.pricingMode = 'flat';
  room.price = mapCalculatedMoneyToInputComponent(roomTotal.structure.price);
  room.pricePeriods = [];
  item.priceStructure.unit = intent.unit;
}

function dividePriceComponent(
  component: NormalizedPriceComponent,
  divisor: number
): NormalizedPriceComponent {
  if (divisor <= 1) return component;

  return {
    ...component,
    cost: divideNullable(component.cost, divisor),
    netCost: divideNullable(component.netCost, divisor),
    unitPrice: divideNullable(component.unitPrice, divisor),
    // Split the base-currency provenance alongside the price it describes; the
    // spread above would otherwise carry the whole-stay value onto every night.
    baseUnitPrice: divideNullable(component.baseUnitPrice ?? null, divisor),
    finalPrice: divideNullable(component.finalPrice, divisor),
    finalPriceWithTax: divideNullable(component.finalPriceWithTax, divisor),
    profit: divideNullable(component.profit, divisor),
  };
}

function divideNullable(value: number | null, divisor: number): number | null {
  return value === null ? null : divide(value, divisor);
}

const SINGLE_STRUCTURE_UNITS = new Set([
  'SINGLE',
  'UNIT',
  'DAY',
  'HOUR',
  'NIGHT',
  'PIECE',
  'VEHICLE',
]);

function extractRepresentativePriceComponent(
  item: NormalizedItemInput
): NormalizedPriceComponent | null {
  const structure = item.priceStructure;
  if (structure.kind === 'single') return structure.price;
  if (structure.kind === 'travellers') {
    return structure.travellers[0]?.price ?? null;
  }
  if (structure.kind === 'group') {
    const threshold = structure.thresholds[0];
    return (
      threshold?.adultPrice ??
      threshold?.childPrice ??
      structure.fallbackPolicy.fallbackPrice ??
      null
    );
  }
  if (structure.kind === 'rooms') {
    return structure.rooms[0]?.price ?? null;
  }
  return null;
}

// Accommodation rows must always carry at least one editable room. Repair any
// rooms structure that arrives (or becomes) empty by injecting a zero-priced
// default room derived from the item's booking dates. Idempotent: only acts on
// empty arrays.
function ensureNonEmptyRoomStructure(input: MutablePricingInput): void {
  for (const item of input.items) {
    const structure = item.priceStructure;
    if (structure.kind !== 'rooms') continue;
    if (structure.rooms.length > 0) continue;
    structure.rooms.push(buildDefaultRoom(item, structure.unit, input));
  }
}

// NIGHT rooms must be period-based so the price-periods column is editable.
// Convert any NIGHT room that has no periods into a single period covering the
// whole stay. Per-night price is preserved (flat NIGHT calc already uses
// multiplier = nights), so the computed total does not change. Deterministic
// period id keeps inline night-count edits stable across recalcs.
function ensureNightRoomsHavePeriods(input: MutablePricingInput): void {
  for (const item of input.items) {
    const structure = item.priceStructure;
    if (structure.kind !== 'rooms') continue;
    for (const room of structure.rooms) {
      if (room.unit !== 'NIGHT') continue;
      if (room.pricePeriods.length > 0) continue;
      const nights = resolveRoomNights(room, item);
      room.pricingMode = 'periods';
      room.pricePeriods = [
        {
          id: `${room.id}-period-1`,
          sourceId: null,
          startDate: null,
          endDate: null,
          startNight: 1,
          nightCount: nights,
          price: { ...room.price },
        },
      ];
    }
  }
}

// Seed item-level accommodation traveller categories from the itinerary
// travellers when the rooms structure has none. Money-neutral; lets existing
// accommodation (which never stored item travellers) render as per-category
// rows. Idempotent: only acts when the set is empty/absent.
function ensureItemTravellers(input: MutablePricingInput): void {
  for (const item of input.items) {
    const structure = item.priceStructure;
    // Every priceable structure that shows a single category column gets the
    // item-level traveller set: accommodation (rooms) AND scalar items
    // (transportation, line items, activities, …). PAX (travellers) and GROUP
    // manage their own traveller lines/thresholds and are left untouched.
    // GROUP also carries a per-item traveller set, but it is priced from those
    // counts, so seed from the SAME itinerary source the group total uses
    // (travellerQuantities preferred) — keeping the initial total identical to
    // the pre-feature input-level count.
    if (structure.kind === 'group') {
      const seed =
        structure.travellers && structure.travellers.length > 0
          ? structure.travellers
          : input.travellerQuantities.length > 0
            ? input.travellerQuantities
            : input.travellers;
      // Preserve the itinerary's plural category labels (travellerQuantities
      // carry none) so the table shows "Adults"/"Children", not the type name.
      const labelByType = new Map<string, string | null>();
      for (const traveller of input.travellers) {
        if (!labelByType.has(traveller.type)) {
          labelByType.set(traveller.type, traveller.label ?? null);
        }
      }
      structure.travellers = collapseTravellersByType(seed).map(
        (traveller) => ({
          ...traveller,
          label: traveller.label ?? labelByType.get(traveller.type) ?? null,
        })
      );
      continue;
    }
    if (structure.kind !== 'rooms' && structure.kind !== 'single') continue;
    // Source = the item's own categories if present, else seed from itinerary
    // travellers. Always collapse to ONE row per traveller type so duplicates
    // from any layer (per-person seed, legacy data, repeated saves) can never
    // render as multiple Adults/Children rows.
    const source =
      structure.travellers && structure.travellers.length > 0
        ? structure.travellers
        : input.travellers;
    structure.travellers = collapseTravellersByType(source);
  }
}

// Collapse travellers to one category per TYPE (Adults, Children, …); the type
// is the category identity and quantity is the head count. Mirrors PAX's
// groupTravellersByType.
function collapseTravellersByType(
  travellers: ReadonlyArray<{
    type: TravellerInput['type'];
    quantity: number;
    label?: string | null;
  }>
): Array<{
  travellerId: string;
  type: TravellerInput['type'];
  quantity: number;
  label: string | null;
}> {
  const byType = new Map<
    string,
    {
      travellerId: string;
      type: TravellerInput['type'];
      quantity: number;
      label: string | null;
    }
  >();
  for (const traveller of travellers) {
    const existing = byType.get(traveller.type);
    if (existing) {
      existing.quantity += traveller.quantity;
      continue;
    }
    byType.set(traveller.type, {
      travellerId: traveller.type,
      type: traveller.type,
      quantity: traveller.quantity,
      label: traveller.label ?? null,
    });
  }
  return [...byType.values()];
}

function buildDefaultRoom(
  item: NormalizedItemInput,
  structureUnit: 'UNIT' | 'DAY' | 'NIGHT',
  input: MutablePricingInput
): RoomPriceInput {
  const nights = item.dateRange?.nights ?? null;
  return {
    id: `${item.id}-room-1`,
    sourceId: null,
    roomTypeId: null,
    label: null,
    unit: structureUnit,
    pricingMode: 'flat',
    pricingSource: {
      mode: 'flat',
      sourceSystem: input.context.source,
      sourcePath: null,
      sourceRoomId: null,
      sourceHasPricePeriods: false,
      originalPeriodCount: 0,
      originalUnit: structureUnit,
      legacySinglePeriodExpandedFromRoomPrice: false,
      defaultNightCount: nights,
    },
    quantity: 1,
    nights,
    price: buildZeroPriceComponent(input.context.baseCurrency),
    pricePeriods: [],
    travellers: [],
  };
}

function buildZeroPriceComponent(currency: string): NormalizedPriceComponent {
  return {
    currency,
    cost: 0,
    netCost: 0,
    unitPrice: 0,
    finalPrice: 0,
    finalPriceWithTax: 0,
    profit: 0,
    markupPercent: 0,
    commissionPercent: 0,
    units: 1,
    costVat: null,
    priceVat: null,
    isGross: false,
    useNetCost: false,
  };
}

// The GROUP row's own price line (legacy `item.price`). Its COST comes from the
// matched tier total, so every price driver starts NULL: an explicit `0` unit
// price outranks the markup in `resolveRawUnitPrice` and would peg the row at 0.
function buildGroupPriceLineComponent(
  currency: string
): NormalizedPriceComponent {
  return {
    ...buildZeroPriceComponent(currency),
    netCost: null,
    unitPrice: null,
    finalPrice: null,
    finalPriceWithTax: null,
    profit: null,
  };
}

// Legacy `createGroupPriceLineWithTotal`: whenever the tier total changes, the
// group's price line is rebuilt from it and only the markup survives. Dropping
// the explicit price drivers hands the price back to `cost x (1 + markup)`; the
// stale pre-migration scalar is dropped with them so it cannot resurface.
function clearGroupPriceDrivers(structure: GroupPriceStructure): void {
  if (structure.cost) {
    structure.cost.unitPrice = null;
    structure.cost.finalPrice = null;
    structure.cost.finalPriceWithTax = null;
    structure.cost.profit = null;
  }
  structure.fallbackPolicy = {
    ...structure.fallbackPolicy,
    mode: 'none',
    fallbackPrice: null,
    preserveForInitialMigration: false,
  };
}

function applySetItemPricingTypeIntent(
  input: MutablePricingInput,
  item: NormalizedItemInput,
  intent: SetItemPricingTypeIntent
): void {
  const targetType = intent.pricingType;
  const structure = item.priceStructure;
  if (structure.kind === 'rooms') return;

  const baseComponent =
    extractRepresentativePriceComponent(item) ??
    buildZeroPriceComponent(input.context.baseCurrency);

  if (targetType === 'PAX') {
    if (structure.kind === 'travellers') return;
    const matchPolicy: TravellersPriceStructure['matchPolicy'] = {
      strategy: 'travellerId',
      missingTravellerBehavior: 'skipPrice',
      preserveLegacyMissingTravellerBehavior: false,
      sourceSystem: input.context.source,
    };
    const travellers = input.travellers.map((traveller) => ({
      travellerId: traveller.id,
      sourceId: traveller.sourceId ?? null,
      type: traveller.type,
      quantity: traveller.quantity,
      price: { ...baseComponent, units: traveller.quantity || 1 },
    }));
    item.priceStructure = {
      kind: 'travellers',
      unit: 'PAX',
      matchPolicy,
      travellers,
    };
    return;
  }

  if (targetType === 'GROUP') {
    if (structure.kind === 'group') return;
    const fallbackPolicy: GroupPriceStructure['fallbackPolicy'] = {
      mode: 'thresholdOnly',
      reviewStatus: 'approved',
      source: 'unknown',
      fallbackPrice: null,
      preserveForInitialMigration: false,
    };
    item.priceStructure = {
      kind: 'group',
      unit: 'GROUP',
      thresholds: [
        {
          // Legacy tier model: the first tier always starts at 1 person (and is
          // editable in the threshold editor). Seeding it with the current
          // traveller total would make smaller groups fall through to a
          // NO_MATCH and is not what the legacy editor defaulted to.
          minTravellers: 1,
          maxTravellers: null,
          travellerId: null,
          additionalTravellerId: null,
          adultPrice: baseComponent,
          childPrice: null,
          additionalAdultPrice: null,
          additionalChildPrice: null,
        },
      ],
      fallbackPolicy,
    };
    return;
  }

  if (SINGLE_STRUCTURE_UNITS.has(targetType)) {
    // Legacy collapsed to the first line and re-applied the item-level
    // pricingUnits (`priceLine.updateUnits(priceRow.pricingUnits)`), so a
    // Unit×N → Pax → Unit round trip restores N.
    const units =
      structure.kind === 'single'
        ? (baseComponent.units ?? 1)
        : (item.pricingUnits ?? 1);
    item.priceStructure = {
      kind: 'single',
      unit: targetType as SinglePriceUnit,
      price: { ...baseComponent, units },
    };
  }
}

function mapCalculatedMoneyToInputComponent(
  money: CalculatedMoneyFields
): NormalizedPriceComponent {
  const rate = money.rate ?? 1;
  return {
    currency: money.sourceCurrency ?? money.currency,
    cost: money.cost,
    netCost: money.netCost,
    unitPrice:
      money.unitPrice === null ? null : multiply(money.unitPrice, rate),
    // `money.unitPrice` is already the base-currency figure, so it is the
    // provenance for the item-currency value scaled above — carry it so an
    // in-memory hop (setRoomUnit) does not reintroduce the round trip.
    baseUnitPrice: money.unitPrice,
    finalPrice: money.finalPrice,
    finalPriceWithTax: money.finalPriceWithTax,
    profit: money.profit,
    markupPercent: money.markupPercent,
    commissionPercent: money.commissionPercent,
    units: money.units !== null && money.units >= 0 ? money.units : null,
    costVat: money.costVat,
    priceVat: money.priceVat,
    isGross: money.isGross,
    useNetCost: money.useNetCost,
  };
}

function mapEditIntentRoomToInput(intent: AddRoomIntent): RoomPriceInput {
  return {
    id: intent.room.id,
    sourceId: intent.room.sourceId ?? null,
    roomTypeId: intent.room.roomTypeId ?? null,
    label: intent.room.label ?? null,
    unit: intent.room.unit,
    pricingMode: intent.room.pricingMode,
    pricingSource: {
      mode: intent.room.pricingMode,
      sourceSystem: 'frontend-edit-state',
      sourcePath: null,
      sourceRoomId: null,
      sourceHasPricePeriods: intent.room.pricePeriods.length > 0,
      originalPeriodCount: intent.room.pricePeriods.length,
      originalUnit: intent.room.unit,
      legacySinglePeriodExpandedFromRoomPrice:
        intent.room.pricingMode === 'legacySinglePeriodExpansion',
      defaultNightCount: intent.room.nights,
    },
    quantity: intent.room.quantity,
    nights: intent.room.nights,
    price: intent.room.price,
    pricePeriods: intent.room.pricePeriods.map(mapEditIntentPeriodToInput),
  };
}

function mapEditIntentPeriodToInput(
  period: EditIntentRoomPeriodPayload
): RoomPricePeriod {
  return {
    id: period.id,
    sourceId: null,
    startDate: period.startDate,
    endDate: period.endDate,
    startNight: period.startNight,
    nightCount: period.nightCount,
    price: period.price,
  };
}

function applyTravellerQuantityIntent(
  input: MutablePricingInput,
  item: NormalizedItemInput,
  intent: SetTravellerQuantityIntent
): void {
  // Accommodation / scalar / group items: set the item-level category total
  // quantity. GROUP prices from these per-item counts (legacy parity), so the
  // edit both displays and drives the recalculated group total.
  if (
    item.priceStructure.kind === 'rooms' ||
    item.priceStructure.kind === 'single' ||
    item.priceStructure.kind === 'group'
  ) {
    for (const traveller of item.priceStructure.travellers ?? []) {
      if (traveller.travellerId === intent.travellerId) {
        traveller.quantity = intent.quantity;
      }
    }
    // Legacy `ItemPriceRow.onChangeTravellersQuantity` rebuilds a GROUP's price
    // line from the re-matched tier total, same as a tier edit.
    if (item.priceStructure.kind === 'group') {
      clearGroupPriceDrivers(item.priceStructure);
    }
    return;
  }

  if (item.priceStructure.kind !== 'travellers') return;

  const structure = item.priceStructure;
  const target = structure.travellers.find(
    (traveller) => traveller.travellerId === intent.travellerId
  );
  if (!target) return;

  // First time an item's PAX quantity is edited, it switches to its OWN
  // (manually-set) traveller list. Snapshot the currently-resolved itinerary
  // quantities onto every line so untouched traveller types keep the value the
  // user currently sees rather than collapsing to a stale line quantity.
  const wasManual =
    structure.matchPolicy.missingTravellerBehavior === 'useLineQuantity';
  if (!wasManual) {
    for (const traveller of structure.travellers) {
      const resolved = findTravellerQuantity(traveller, structure, input);
      if (resolved !== null) traveller.quantity = resolved;
    }
  }

  // The Travellers column shows the per-TYPE total (same-type lines are summed
  // for display). Set that type's total to exactly the entered value: the
  // edited line takes the value and its same-type siblings are zeroed, so the
  // summed display can never double. The edit is item-local and does NOT mutate
  // the shared itinerary traveller counts, so other items stay unchanged.
  for (const traveller of structure.travellers) {
    if (traveller.type !== target.type) continue;
    traveller.quantity = traveller === target ? intent.quantity : 0;
  }

  if (!wasManual) {
    structure.matchPolicy = {
      ...structure.matchPolicy,
      missingTravellerBehavior: 'useLineQuantity',
      preserveLegacyMissingTravellerBehavior: false,
    };
  }
}

function applyAddTravellerLineIntent(
  input: MutablePricingInput,
  item: NormalizedItemInput,
  intent: AddTravellerLineIntent
): void {
  // Accommodation / scalar items: add a traveller CATEGORY (by type) to the
  // item-level set. intent.travellerId carries the category type; quantity is
  // the head count for that type across the itinerary.
  if (
    item.priceStructure.kind === 'rooms' ||
    item.priceStructure.kind === 'single'
  ) {
    const structure = item.priceStructure;
    if (!structure.travellers) structure.travellers = [];
    if (
      structure.travellers.some((t) => t.travellerId === intent.travellerId)
    ) {
      return;
    }
    const sameType = input.travellers.filter(
      (traveller) => traveller.type === intent.travellerId
    );
    const type = (sameType[0]?.type ??
      intent.travellerId) as TravellerInput['type'];
    const quantity =
      sameType.reduce((sum, traveller) => sum + traveller.quantity, 0) ||
      intent.quantity;
    structure.travellers.push({
      travellerId: intent.travellerId,
      type,
      quantity,
      label: sameType[0]?.label ?? null,
    });
    return;
  }

  if (item.priceStructure.kind !== 'travellers') return;

  const existingTraveller = item.priceStructure.travellers.find(
    (traveller) => traveller.travellerId === intent.travellerId
  );
  if (existingTraveller) return;

  const sourceTraveller = input.travellers.find(
    (traveller) => traveller.id === intent.travellerId
  );
  if (!sourceTraveller) return;

  const templatePrice = item.priceStructure.travellers[0]?.price;
  const travellerPrice: TravellerPriceInput = {
    travellerId: sourceTraveller.id,
    sourceId: sourceTraveller.sourceId ?? null,
    type: sourceTraveller.type,
    quantity: intent.quantity,
    price: {
      ...(templatePrice ??
        createZeroPriceComponent(input.context.baseCurrency)),
      units: intent.quantity,
    },
  };

  item.priceStructure.travellers.push(travellerPrice);
  upsertTravellerQuantity(input, travellerPrice, intent.quantity);
}

function applyRemoveTravellerLineIntent(
  item: NormalizedItemInput,
  intent: RemoveTravellerLineIntent
): void {
  // Accommodation / scalar items: remove a traveller category from the
  // item-level set (and from per-room distribution for rooms, so no orphan
  // remains). Never remove the last category (legacy disables delete at one).
  if (
    item.priceStructure.kind === 'rooms' ||
    item.priceStructure.kind === 'single'
  ) {
    const structure = item.priceStructure;
    const current = structure.travellers ?? [];
    if (current.length <= 1) return;
    structure.travellers = current.filter(
      (traveller) => traveller.travellerId !== intent.travellerId
    );
    if (structure.kind === 'rooms') {
      for (const room of structure.rooms) {
        if (!room.travellers) continue;
        room.travellers = room.travellers.filter(
          (traveller) => traveller.travellerId !== intent.travellerId
        );
      }
    }
    return;
  }

  if (item.priceStructure.kind !== 'travellers') return;

  item.priceStructure.travellers = item.priceStructure.travellers.filter(
    (traveller) => traveller.travellerId !== intent.travellerId
  );
}

function upsertTravellerQuantity(
  input: MutablePricingInput,
  traveller: TravellerPriceInput,
  quantity: number
): void {
  const existing = input.travellerQuantities.find(
    (candidate) => candidate.travellerId === traveller.travellerId
  );

  if (existing) {
    existing.quantity = quantity;
    existing.type = traveller.type;
    return;
  }

  input.travellerQuantities.push({
    travellerId: traveller.travellerId,
    type: traveller.type,
    quantity,
  });
}

// VAT is an ITEM-level fact: persistence stores exactly one `costVatId` /
// `priceVatId` per item, and the pricing table says as much in the UI ("VAT is
// shared across the item's rooms — edit it on the first"). So a VAT intent
// ignores the row/period/traveller targeting and rewrites every component.
//
// Without this the sub-rows keep their old VAT, and the persist step — which
// resolves the item's FK by taking the first NON-NULL vat id found anywhere in
// the structure — reads a stale id off room 2 the moment room 1 goes to 0%
// (whose id is null and therefore skipped). That made 0% impossible to save on
// any multi-room accommodation: it silently came back as the previous rate
// (XT-623). Setting a NON-zero rate appeared to work only because room 1's id
// happened to sort first.
const isItemWideIntent = (intent: PricingEditIntent): boolean =>
  intent.type === 'setCostVat' || intent.type === 'setPriceVat';

function forEachTargetComponent(
  structure: NormalizedPriceStructure,
  intent: PricingEditIntent,
  visit: (component: NormalizedPriceComponent) => void
): void {
  const itemWide = isItemWideIntent(intent);
  switch (structure.kind) {
    case 'single':
      visit(structure.price);
      return;
    case 'rooms':
      for (const room of structure.rooms) {
        if (
          !itemWide &&
          intent.target.roomId &&
          room.id !== intent.target.roomId
        )
          continue;
        if (itemWide || !intent.target.periodId) visit(room.price);
        for (const period of room.pricePeriods) {
          if (
            !itemWide &&
            intent.target.periodId &&
            period.id !== intent.target.periodId
          )
            continue;
          visit(period.price);
        }
      }
      return;
    case 'travellers':
      for (const traveller of structure.travellers) {
        if (
          !itemWide &&
          intent.target.travellerId &&
          traveller.travellerId !== intent.target.travellerId
        ) {
          continue;
        }
        visit(traveller.price);
      }
      return;
    case 'group': {
      // Item-level money edits (cost / VAT / commission / currency / gross) for
      // a GROUP hit its single flat cost; the per-tier SELLING prices are edited
      // ONLY through `setGroupThresholds`, never component edits — so a setCost
      // here must not recompute the thresholds' unit prices. Fall back to the
      // legacy migration scalar when no flat cost is present.
      const groupComponent =
        structure.cost ?? structure.fallbackPolicy.fallbackPrice ?? null;
      visitNullableComponent(groupComponent, visit);
      return;
    }
    default:
      return;
  }
}

function visitNullableComponent(
  component: NormalizedPriceComponent | null,
  visit: (component: NormalizedPriceComponent) => void
): void {
  if (component) visit(component);
}

// Maintains `baseUnitPrice` (the typed base-currency provenance) around the
// per-intent edits. Doing it here rather than inside each branch means a new
// intent cannot silently leave stale provenance behind: anything that moves or
// clears the item-currency price invalidates it by construction.
function applyComponentEdit(
  component: NormalizedPriceComponent,
  intent: PricingEditIntent,
  rate: number
): void {
  const previousUnitPrice = component.unitPrice;
  applyComponentEditFields(component, intent, rate);

  if (intent.type === 'setUnitPrice') {
    component.baseUnitPrice = intent.value;
    return;
  }

  // A currency swap re-rates the row, so the old base figure no longer
  // describes this price.
  if (
    intent.type === 'setCurrency' ||
    component.unitPrice !== previousUnitPrice
  ) {
    component.baseUnitPrice = null;
  }
}

function applyComponentEditFields(
  component: NormalizedPriceComponent,
  intent: PricingEditIntent,
  rate: number
): void {
  switch (intent.type) {
    case 'setCost': {
      // V2 rule: a cost edit holds the markup and re-derives the price. On a
      // price-driven row the stored markup can be a 2dp echo of the real
      // price/cost ratio (legacy persisted markup rounded), and re-deriving
      // from the echo shifts a whole entered price by a cent (490 → 490.01).
      // Recover the exact ratio from the actual money first — but only when
      // the stored markup IS that echo; a deliberately divergent markup keeps
      // V2's literal hold-the-markup behavior (see the "wild markup" test:
      // price 760 with markup null must not back-compute 660%).
      const previousCost = component.cost ?? 0;
      if (
        component.unitPrice !== null &&
        previousCost > 0 &&
        component.markupPercent !== null
      ) {
        const priceImpliedMarkup = percentage(
          subtract(component.unitPrice, previousCost),
          previousCost
        );
        if (
          Math.abs(priceImpliedMarkup - component.markupPercent) <=
          LEGACY_PERCENT_ECHO_TOLERANCE
        ) {
          component.markupPercent = priceImpliedMarkup;
        }
      }
      component.markupPercent = component.markupPercent ?? 0;
      component.cost = intent.value;
      if (intent.value === 0) {
        component.markupPercent = 0;
        component.commissionPercent = 0;
      }
      // Markup is the driver from here: leave unitPrice/netCost null so
      // calculateMoney derives the price (resolveRawUnitPrice) and net cost
      // (resolveNetCost) from the exact percents — and echoes those percents
      // back instead of re-deriving them from quantized money.
      component.netCost = null;
      component.unitPrice = null;
      component.finalPrice = null;
      component.profit = null;
      return;
    }
    case 'setMarkup':
      component.markupPercent = intent.valuePercent;
      // The typed markup is the driver: keep the component markup-driven so
      // the exact typed percent survives calculation and persistence; the
      // price derives in resolveRawUnitPrice.
      component.unitPrice = null;
      component.finalPrice = null;
      component.profit = null;
      return;
    case 'setCommission': {
      component.commissionPercent = intent.valuePercent;
      // Commission drives netCost: derive it in resolveNetCost so the exact
      // typed percent echoes through calculation and persistence.
      component.netCost = null;
      return;
    }
    case 'setUnitPrice': {
      const scaledUnitPrice = multiply(intent.value, rate);
      component.unitPrice = scaledUnitPrice;
      component.finalPrice = null;
      component.profit = null;
      component.markupPercent =
        (component.cost ?? 0) === 0
          ? 0
          : percentage(
              subtract(scaledUnitPrice, component.cost ?? 0),
              component.cost ?? 0
            );
      return;
    }
    case 'setFinalPrice':
      component.unitPrice = null;
      component.finalPrice = intent.value;
      component.profit = null;
      return;
    case 'setProfit':
      component.unitPrice = null;
      component.finalPrice = null;
      component.profit = intent.value;
      return;
    case 'setUnits':
      component.units = intent.value;
      return;
    case 'setCostVat':
      component.costVat = intent.vat;
      // netCost re-derives from the new VAT (and the commission percent) in
      // resolveNetCost — leaving it null keeps the commission percent as the
      // driver so the exact typed value echoes through persistence.
      component.netCost = null;
      return;
    case 'setPriceVat':
      component.priceVat = intent.vat;
      return;
    case 'setCurrency':
      component.currency = intent.currency;
      return;
    case 'setPriceType': {
      component.isGross = intent.isGross;
      if (!intent.isGross) {
        component.commissionPercent = 0;
        component.netCost = excludeVatFromAmount(
          component.cost ?? 0,
          component.costVat
        );
      }
      return;
    }
    case 'setTravellerQuantity':
    case 'setGroupThresholds':
    case 'setNightPeriod':
    case 'setRoomUnit':
    case 'addTravellerLine':
    case 'removeTravellerLine':
    case 'addRoom':
    case 'deleteRoom':
    case 'addRoomPeriod':
    case 'deleteRoomPeriod':
      return;
    default:
      return;
  }
}

function calculateItemTotal(
  item: NormalizedItemInput,
  input: PricingInput,
  diagnostics: PricingDiagnostic[]
): ItemTotal {
  const inclusion = resolveInclusion(item.flags, input.inclusionPolicies);
  const structureTotal = calculateStructureTotal(item, input, diagnostics);
  warnForNegativeMoney(item, structureTotal.money, diagnostics);

  return {
    item,
    inclusion,
    ...structureTotal,
  };
}

function calculateStructureTotal(
  item: NormalizedItemInput,
  input: PricingInput,
  diagnostics: PricingDiagnostic[]
): StructureTotal {
  switch (item.priceStructure.kind) {
    case 'single':
      return calculateSingleTotal(
        item.priceStructure.price,
        item,
        input,
        diagnostics
      );
    case 'rooms':
      return calculateRoomsTotal(
        item.priceStructure.rooms,
        item,
        input,
        diagnostics
      );
    case 'travellers':
      return calculateTravellersTotal(
        item.priceStructure,
        item,
        input,
        diagnostics
      );
    case 'group':
      return calculateGroupTotal(item.priceStructure, item, input, diagnostics);
    default:
      return zeroStructureTotal(input.context.baseCurrency, {
        kind: 'single',
        unit: 'SINGLE',
        price: zeroCalculatedMoney(input.context.baseCurrency),
      });
  }
}

function calculateSingleTotal(
  component: NormalizedPriceComponent,
  item: NormalizedItemInput,
  input: PricingInput,
  diagnostics: PricingDiagnostic[]
): StructureTotal {
  // A single priced line always represents at least one unit. The `?? 1`
  // default only covers null/undefined, but a stored `0` (e.g. an item
  // created in the planner from a product whose `pricingUnits` is 0/unset,
  // then cemented by a pricing save) would otherwise render as "0 Units" and
  // zero the line total. Clamp non-positive counts to 1 — PAX / rooms counts
  // live in their own totals where 0 can be legitimate, so this stays scoped
  // to single-line pricing.
  // Per-day pricing (rental cars) derives its multiplier from the booking span:
  // the daily rate is charged once per calendar day, pickup→drop-off INCLUSIVE,
  // mirroring how NIGHT rooms derive nights — and matching the rental day count
  // shown elsewhere in the itinerary. Date-less day items fall back to the
  // stored unit count.
  const isDayUnit =
    item.priceStructure.kind === 'single' && item.priceStructure.unit === 'DAY';
  const rawUnits = isDayUnit
    ? resolveDayCount(item, component)
    : (component.units ?? 1);
  const units = rawUnits > 0 ? rawUnits : 1;
  const calculation = calculateMoney(
    component,
    units,
    1,
    input,
    item,
    diagnostics
  );
  return {
    money: calculation.money,
    commissionAmount: calculation.commissionAmount,
    finalCommissionAmount: calculation.finalCommissionAmount,
    finalCost: calculation.finalCost,
    quantity: units,
    structure: {
      kind: 'single',
      unit:
        item.priceStructure.kind === 'single'
          ? item.priceStructure.unit
          : 'SINGLE',
      price: calculation.money,
      ...(item.priceStructure.kind === 'single' &&
      item.priceStructure.travellers
        ? { travellers: item.priceStructure.travellers }
        : {}),
    },
  };
}

function calculateRoomsTotal(
  rooms: RoomPriceInput[],
  item: NormalizedItemInput,
  input: PricingInput,
  diagnostics: PricingDiagnostic[]
): StructureTotal {
  const roomCalculations = rooms.map((room) =>
    calculateRoomTotal(room, item, input, diagnostics)
  );

  return {
    money: sumMoney(
      roomCalculations.map((room) => room.money),
      input.context.baseCurrency
    ),
    commissionAmount: sumNumbers(
      roomCalculations.map((room) => room.commissionAmount)
    ),
    finalCommissionAmount: sumNumbers(
      roomCalculations.map((room) => room.finalCommissionAmount)
    ),
    finalCost: sumNumbers(roomCalculations.map((room) => room.finalCost)),
    quantity: sumNumbers(roomCalculations.map((room) => room.quantity)),
    roomIds: rooms.map((room) => room.id),
    structure: {
      kind: 'rooms',
      unit: rooms[0]?.unit ?? 'UNIT',
      rooms: roomCalculations.map((room) => room.structure),
      ...(item.priceStructure.kind === 'rooms' && item.priceStructure.travellers
        ? { travellers: item.priceStructure.travellers }
        : {}),
    },
  };
}

function calculateRoomTotal(
  room: RoomPriceInput,
  item: NormalizedItemInput,
  input: PricingInput,
  diagnostics: PricingDiagnostic[]
): {
  money: CalculatedMoneyFields;
  commissionAmount: number;
  finalCommissionAmount: number;
  finalCost: number;
  quantity: number;
  structure: {
    roomId: string;
    sourceId: string | null;
    roomTypeId: string | null;
    label?: string | null;
    unit: 'UNIT' | 'DAY' | 'NIGHT';
    pricingMode: 'flat' | 'periods' | 'legacySinglePeriodExpansion';
    quantity: number | null;
    nights: number | null;
    travellers?: RoomPriceInput['travellers'];
    price: CalculatedMoneyFields;
    pricePeriods: Array<{
      periodId: string | null;
      sourceId: string | null;
      startDate: string | null;
      endDate: string | null;
      startNight: number | null;
      nightCount: number;
      displayLabel?: string | null;
      price: CalculatedMoneyFields;
    }>;
  };
} {
  const units = room.quantity ?? 1;
  const nights = resolveRoomNights(room, item);

  if (room.unit === 'NIGHT' && room.pricePeriods.length > 0) {
    const periodCalculations: RoomPeriodCalculation[] = room.pricePeriods.map(
      (period) => {
        const periodNightCount = resolvePeriodNightCount(
          room,
          period,
          nights,
          diagnostics,
          item
        );
        const calculation = calculateMoney(
          period.price,
          units,
          periodNightCount,
          input,
          item,
          diagnostics
        );
        return {
          period,
          periodNightCount,
          calculation,
        };
      }
    );
    const periodPersistencePrice = buildRoomPeriodPersistencePrice(
      periodCalculations,
      input.context.baseCurrency
    );

    return {
      money: sumMoney(
        periodCalculations.map((period) => period.calculation.money),
        input.context.baseCurrency
      ),
      commissionAmount: sumNumbers(
        periodCalculations.map((period) => period.calculation.commissionAmount)
      ),
      finalCommissionAmount: sumNumbers(
        periodCalculations.map(
          (period) => period.calculation.finalCommissionAmount
        )
      ),
      finalCost: sumNumbers(
        periodCalculations.map((period) => period.calculation.finalCost)
      ),
      quantity: units,
      structure: {
        roomId: room.id,
        sourceId: room.sourceId ?? null,
        roomTypeId: room.roomTypeId ?? null,
        label: room.label ?? null,
        unit: room.unit,
        pricingMode: room.pricingMode,
        quantity: units,
        nights,
        travellers: room.travellers ?? null,
        price: periodPersistencePrice,
        pricePeriods: periodCalculations.map(
          ({ period, periodNightCount, calculation }) => ({
            periodId: period.id ?? null,
            sourceId: period.sourceId ?? null,
            startDate: period.startDate,
            endDate: period.endDate,
            startNight: period.startNight,
            nightCount: periodNightCount,
            displayLabel: formatRoomPeriodDisplayLabel({
              itemStartDate: clampStartToItineraryStart(
                item.dateRange?.startDate ?? item.startTime ?? null,
                input.context.itineraryStartTime ?? null
              ),
              period,
              periodNightCount,
            }),
            price: calculation.money,
          })
        ),
      },
    };
  }

  const multiplier = room.unit === 'UNIT' ? 1 : nights;
  const calculation = calculateMoney(
    room.price,
    units,
    multiplier,
    input,
    item,
    diagnostics
  );

  return {
    money: calculation.money,
    commissionAmount: calculation.commissionAmount,
    finalCommissionAmount: calculation.finalCommissionAmount,
    finalCost: calculation.finalCost,
    quantity: units,
    structure: {
      roomId: room.id,
      sourceId: room.sourceId ?? null,
      roomTypeId: room.roomTypeId ?? null,
      label: room.label ?? null,
      unit: room.unit,
      pricingMode: room.pricingMode,
      quantity: units,
      nights,
      travellers: room.travellers ?? null,
      price: calculation.money,
      pricePeriods: [],
    },
  };
}

function buildRoomPeriodPersistencePrice(
  periodCalculations: RoomPeriodCalculation[],
  currency: CurrencyCode
): CalculatedMoneyFields {
  if (periodCalculations.length === 0) return zeroCalculatedMoney(currency);

  const commonRate = getCommonRate(
    periodCalculations.map(({ calculation }) => calculation.money)
  );

  const summedFinalCost = sumNumbers(
    periodCalculations.map(({ calculation }) => calculation.finalCost)
  );

  return {
    currency,
    sourceCurrency: getCommonSourceCurrency(
      periodCalculations.map(({ calculation }) => calculation.money)
    ),
    ...(commonRate !== undefined ? { rate: commonRate } : {}),
    finalCost: summedFinalCost,
    cost: sumNumbers(
      periodCalculations.map(({ calculation, periodNightCount }) =>
        multiply(calculation.money.cost ?? 0, periodNightCount)
      )
    ),
    netCost: sumNumbers(
      periodCalculations.map(({ calculation, periodNightCount }) =>
        multiply(calculation.money.netCost ?? 0, periodNightCount)
      )
    ),
    finalNetCost: sumNumbers(
      periodCalculations.map(
        ({ calculation }) => calculation.money.finalNetCost ?? 0
      )
    ),
    unitPrice: sumNumbers(
      periodCalculations.map(({ calculation, periodNightCount }) =>
        multiply(calculation.money.unitPrice ?? 0, periodNightCount)
      )
    ),
    sourceUnitPrice: sumNumbers(
      periodCalculations.map(({ calculation, periodNightCount }) =>
        multiply(
          calculation.money.sourceUnitPrice ?? calculation.money.unitPrice ?? 0,
          periodNightCount
        )
      )
    ),
    finalPrice: sumNumbers(
      periodCalculations.map(
        ({ calculation }) => calculation.money.finalPrice ?? 0
      )
    ),
    finalPriceWithTax: sumNumbers(
      periodCalculations.map(
        ({ calculation }) => calculation.money.finalPriceWithTax ?? 0
      )
    ),
    profit: sumNumbers(
      periodCalculations.map(({ calculation }) => calculation.money.profit ?? 0)
    ),
    markupPercent: null,
    commissionPercent: null,
    units: periodCalculations[0]?.calculation.money.units ?? 1,
    costVat: periodCalculations[0]?.calculation.money.costVat ?? ZERO_VAT,
    priceVat: periodCalculations[0]?.calculation.money.priceVat ?? ZERO_VAT,
    isGross: periodCalculations.some(
      ({ calculation }) => calculation.money.isGross
    ),
    useNetCost: periodCalculations.some(
      ({ calculation }) => calculation.money.useNetCost
    ),
  };
}

function resolvePeriodNightCount(
  room: RoomPriceInput,
  period: RoomPricePeriod,
  roomNights: number,
  diagnostics: PricingDiagnostic[],
  item: NormalizedItemInput
): number {
  const isLegacySingleDefaultPeriod =
    room.pricingMode === 'legacySinglePeriodExpansion' ||
    (room.unit === 'NIGHT' &&
      room.pricePeriods.length === 1 &&
      period.nightCount === 1 &&
      period.startNight === 1 &&
      roomNights > 1);

  if (isLegacySingleDefaultPeriod) {
    diagnostics.push(
      createDiagnostic({
        category: 'legacy_compatible_warning',
        severity: 'warning',
        code: 'LEGACY_SINGLE_PERIOD_EXPANSION',
        message:
          'Legacy single night period was expanded to the room night count.',
        itemId: item.id,
        lineId: period.id,
        blocksPersistence: false,
      })
    );
    return roomNights;
  }

  return period.nightCount ?? 1;
}

// Per-day items (rental cars) charge the daily rate once per calendar day,
// pickup→drop-off INCLUSIVE — Jun 21 → Jun 23 is 3 days — matching the rental
// duration shown in the itinerary (`utcCalendarDaysInclusive`). Derived from the
// item's own date span so a date edit reprices automatically; falls back to the
// stored unit count when the item has no usable dates.
function resolveDayCount(
  item: NormalizedItemInput,
  component: NormalizedPriceComponent
): number {
  const start = parseDateValue(item.dateRange?.startDate);
  const end = parseDateValue(item.dateRange?.endDate);
  if (start && end) {
    const days = differenceInUtcCalendarDays(end, start) + 1;
    if (days > 0) return days;
  }
  return component.units ?? 1;
}

function resolveRoomNights(
  room: RoomPriceInput,
  item: NormalizedItemInput
): number {
  // Legacy parity: both legacy surfaces clamped accommodation nights to a
  // minimum of one (`Math.max(1, differenceInCalendarDays(...))`), so a
  // same-day stay still charges one night instead of zeroing the room.
  return Math.max(
    room.nights ??
      item.dateRange?.nights ??
      room.pricingSource.defaultNightCount ??
      1,
    1
  );
}

function calculateTravellersTotal(
  structure: TravellersPriceStructure,
  item: NormalizedItemInput,
  input: PricingInput,
  diagnostics: PricingDiagnostic[]
): StructureTotal {
  const travellerCalculations = structure.travellers.map((traveller) => {
    const quantity = resolveTravellerQuantity(
      traveller,
      structure,
      input,
      item,
      diagnostics
    );
    const calculation = calculateMoney(
      traveller.price,
      quantity,
      1,
      input,
      item,
      diagnostics
    );
    return { traveller, quantity, calculation };
  });

  return {
    money: sumMoney(
      travellerCalculations.map((traveller) => traveller.calculation.money),
      input.context.baseCurrency
    ),
    commissionAmount: sumNumbers(
      travellerCalculations.map(
        (traveller) => traveller.calculation.commissionAmount
      )
    ),
    finalCommissionAmount: sumNumbers(
      travellerCalculations.map(
        (traveller) => traveller.calculation.finalCommissionAmount
      )
    ),
    finalCost: sumNumbers(
      travellerCalculations.map((traveller) => traveller.calculation.finalCost)
    ),
    quantity: sumNumbers(
      travellerCalculations.map((traveller) => traveller.quantity)
    ),
    travellerIds: structure.travellers.map(
      (traveller) => traveller.travellerId
    ),
    structure: {
      kind: 'travellers',
      unit: 'PAX',
      matchPolicy: structure.matchPolicy,
      travellers: travellerCalculations.map(
        ({ traveller, quantity, calculation }) => ({
          travellerId: traveller.travellerId,
          sourceId: traveller.sourceId ?? null,
          label: resolveTravellerDisplayLabel(
            input,
            traveller.travellerId,
            traveller.type
          ),
          type: traveller.type,
          quantity,
          price: calculation.money,
        })
      ),
    },
  };
}

function resolveTravellerQuantity(
  travellerPrice: TravellerPriceInput,
  structure: TravellersPriceStructure,
  input: PricingInput,
  item: NormalizedItemInput,
  diagnostics: PricingDiagnostic[]
): number {
  // Manually-set items own their traveller lines: the line quantity is
  // authoritative and must NOT be derived from the shared itinerary traveller
  // counts. This keeps each item's traveller quantity independent (editing one
  // item never changes another) and preserves the exact entered value — even
  // when a line's id coincides with an itinerary traveller id.
  if (structure.matchPolicy.missingTravellerBehavior === 'useLineQuantity') {
    return travellerPrice.quantity ?? 1;
  }

  const quantity = findTravellerQuantity(travellerPrice, structure, input);
  if (quantity !== null) return quantity;

  if (structure.matchPolicy.missingTravellerBehavior === 'zeroPrice') return 0;

  if (structure.matchPolicy.missingTravellerBehavior === 'skipPrice') return 0;

  const fallbackQuantity = travellerPrice.quantity ?? 1;

  diagnostics.push(
    createDiagnostic({
      category: 'legacy_compatible_warning',
      severity: 'warning',
      code: 'LEGACY_MISSING_TRAVELLER_QUANTITY',
      message: `The "${formatTravellerType(travellerPrice.type)}" price line is not linked to any itinerary traveller — its saved quantity (${fallbackQuantity}) was used.`,
      itemId: item.id,
      blocksPersistence: false,
      details: {
        travellerId: travellerPrice.travellerId,
        travellerType: travellerPrice.type,
        fallbackQuantity,
      },
    })
  );
  return fallbackQuantity;
}

function findTravellerQuantity(
  travellerPrice: TravellerPriceInput,
  structure: TravellersPriceStructure,
  input: PricingInput
): number | null {
  const direct = input.travellerQuantities.find(
    (quantity) => quantity.travellerId === travellerPrice.travellerId
  );
  if (direct) return direct.quantity;

  const traveller = input.travellers.find((candidate) =>
    travellerMatches(candidate, travellerPrice, structure.matchPolicy.strategy)
  );
  if (traveller) return traveller.quantity;

  return null;
}

function travellerMatches(
  traveller: TravellerInput,
  travellerPrice: TravellerPriceInput,
  strategy: TravellersPriceStructure['matchPolicy']['strategy']
): boolean {
  switch (strategy) {
    case 'travellerId':
      return traveller.id === travellerPrice.travellerId;
    case 'sourceTravellerId':
      return traveller.sourceId === travellerPrice.sourceId;
    case 'travellerType':
    case 'legacyTravellerType':
      return traveller.type === travellerPrice.type;
    default:
      return false;
  }
}

function calculateGroupTotal(
  structure: GroupPriceStructure,
  item: NormalizedItemInput,
  input: PricingInput,
  diagnostics: PricingDiagnostic[]
): StructureTotal {
  // The pre-migration scalar fallback exists for a GROUP that has no tiers to
  // price from. Real thresholds always win: honouring the scalar alongside them
  // made every tier edit a no-op (the row kept the stale DB number), which is
  // the state EVERY V2-saved group arrives in. The adapter already gates this,
  // the guard here covers any other producer of a pricing input.
  if (
    structure.thresholds.length === 0 &&
    structure.fallbackPolicy.mode === 'legacyDbScalarFallback' &&
    structure.fallbackPolicy.preserveForInitialMigration &&
    structure.fallbackPolicy.fallbackPrice
  ) {
    // A tier-less legacy record priced by its stored scalar WORKS — a working
    // record shows no warning (the old FE_GROUP_DB_FALLBACK_PRESERVED
    // migration notice was noise the user could not act on). The fallback
    // policy metadata still round-trips for review tooling.
    const calculation = calculateMoney(
      structure.fallbackPolicy.fallbackPrice,
      structure.fallbackPolicy.fallbackPrice.units ?? 1,
      1,
      input,
      item,
      diagnostics
    );
    return {
      money: calculation.money,
      commissionAmount: calculation.commissionAmount,
      finalCommissionAmount: calculation.finalCommissionAmount,
      finalCost: calculation.finalCost,
      quantity: calculation.money.units ?? 0,
      thresholdIds: structure.thresholds.map((threshold) => threshold.id ?? ''),
      structure: buildPersistenceGroupStructure(
        structure,
        input,
        item,
        diagnostics
      ),
    };
  }

  // Per-item counts (legacy parity): the group is priced from its own
  // traveller set (seeded from the itinerary by ensureItemTravellers, editable
  // per row), not the itinerary-wide totals.
  const travellerCounts = getGroupTravellerCountsFromList(
    structure.travellers ?? []
  );
  const totalTravellers = travellerCounts.total;

  if (structure.thresholds.length === 0) {
    // Nothing can price this row — neither tiers nor a legacy scalar. This IS
    // actionable, so say exactly what to do.
    diagnostics.push(
      createDiagnostic({
        category: 'missing_data_warning',
        severity: 'warning',
        code: 'GROUP_NO_PRICE_TIERS',
        message:
          'This Per group item has no price tiers. Add at least one threshold row (Min/Max People + Cost) to price it.',
        itemId: item.id,
        blocksPersistence: false,
      })
    );
    return zeroStructureTotal(input.context.baseCurrency, {
      kind: 'group',
      unit: 'GROUP',
      fallbackPolicy: structure.fallbackPolicy,
      thresholds: [],
    });
  }

  // Per-category MATCHED-TIER model (T-198, superseding the cumulative
  // positional bands of 2026-07-31): every traveller category — a specific
  // type or All — carries its own threshold chain; the band whose [Min, Max]
  // contains the category's head count prices every one of those travellers
  // at its price plus its Additional Cost. The group total is the sum over
  // every category chain. Reported case: 1–1 @923, 2–2 @461 with 2 pax →
  // 461 × 2 = 922.
  //
  // GROUP commission lives on the flat `structure.cost` (set via the row's
  // Commission % cell / `setCommission`, persisted in the item price JSON) and
  // is applied ONCE, by the row calculation below — the tier components only
  // supply the cost basis, so applying it to them as well would double-count.
  const componentCalculations = calculateGroupThresholdComponents(
    structure.thresholds,
    travellerCounts,
    input,
    item,
    diagnostics
  );
  const totalMoney = sumMoney(
    componentCalculations.map((component) => component.money),
    input.context.baseCurrency
  );
  const negativeTotal =
    totalMoney.finalPrice !== null && totalMoney.finalPrice < 0;
  const clampedMoney = negativeTotal
    ? zeroCalculatedMoney(input.context.baseCurrency)
    : totalMoney;

  if (negativeTotal) {
    diagnostics.push(
      createDiagnostic({
        category: 'legacy_compatible_warning',
        severity: 'warning',
        code: 'GROUP_NEGATIVE_TOTAL_CLAMPED',
        message:
          'Negative GROUP total was clamped to zero for legacy compatibility.',
        itemId: item.id,
        blocksPersistence: false,
      })
    );
  }

  // Legacy parity — `ItemPriceRow.onGroupPriceThresholdsChange` calls
  // `createGroupPriceLineWithTotal(priceLine, calculateGroupPriceTotal(...))`,
  // i.e. the matched tier total becomes the row's COST (V2's
  // `PricingLine.changeCost`), and the row's own price line then drives what is
  // sold: markup %, a typed unit/final price, profit, commission, VAT and the
  // Net/Gross flag. `structure.cost` IS that price line (adapter: legacy
  // `item.price` / `priceFromDb`), and it is where `forEachTargetComponent`
  // routes every inline money edit on a GROUP row.
  //
  // Deriving the whole row from the tiers instead (cost = Σ tier cost, price =
  // Σ tier unit price) looked right only because a tier component always
  // carries cost === unitPrice (the adapter maps one DB number into both, and
  // the editor writes all three from one field) — so cost === revenue made
  // Profit permanently 0 and Markup permanently 0, and every money-cell edit
  // was silently discarded because nothing read `structure.cost` for value.
  //
  // The tier total is `Σ tier amount × matched count` — exactly legacy
  // `calculateGroupPrice`. `sourceUnitPrice` is that amount in the tiers' own
  // currency (a tier carries ONE number, which the adapter fans out to
  // cost/netCost/price); summing in source currency keeps `calculateMoney`
  // below as the single conversion point.
  const thresholdSourceCost = negativeTotal
    ? 0
    : sumNumbers(
        componentCalculations.map((component) =>
          multiply(
            safeNumber(component.money.sourceUnitPrice),
            component.money.units ?? 1
          )
        )
      );
  // While tiers exist, the legacy scalar never overrides, alters, or joins the
  // calculation — the row prices from the bands and its own selling drivers.
  // (The scalar prices ONLY the tier-less early-return above.)
  const groupPriceLine =
    structure.cost ??
    buildGroupPriceLineComponent(
      structure.thresholds[0]?.adultPrice?.currency ??
        input.context.baseCurrency
    );
  const groupBasis: NormalizedPriceComponent = {
    ...groupPriceLine,
    cost: thresholdSourceCost,
    // The commission % (when set) derives the net cost; an explicit netCost
    // carried over from the flat line would be a stale absolute against the
    // previous cost basis.
    netCost: null,
    // A GROUP price line persists `price: null` (so the reader never mistakes
    // it for a legacy scalar) and every producer — the read adapter, the draft
    // helpers, `buildZeroPriceComponent` — coerces that to 0. An explicit 0
    // outranks the markup in `resolveRawUnitPrice`, which would peg every group
    // at 0, so for a GROUP a zero unit price means "not set".
    unitPrice: groupPriceLine.unitPrice === 0 ? null : groupPriceLine.unitPrice,
    // Legacy `item-to-price-row.util.ts`: `priceFromDb?.markup ?? 0`. Without
    // this a markup-less group falls through `resolveRawUnitPrice` to 0
    // instead of selling at cost.
    markupPercent: groupPriceLine.markupPercent ?? 0,
    finalPriceWithTax: null,
    units: 1,
  };
  const groupCalculation = calculateMoney(
    groupBasis,
    1,
    1,
    input,
    item,
    diagnostics
  );

  const groupDisplayMoney: CalculatedMoneyFields = negativeTotal
    ? clampedMoney
    : groupCalculation.money;

  return {
    money: groupDisplayMoney,
    commissionAmount: negativeTotal ? 0 : groupCalculation.commissionAmount,
    finalCommissionAmount: negativeTotal
      ? 0
      : groupCalculation.finalCommissionAmount,
    finalCost: negativeTotal ? 0 : groupCalculation.finalCost,
    quantity: totalTravellers,
    thresholdIds: structure.thresholds.map((threshold) => threshold.id ?? ''),
    structure: buildPersistenceGroupStructure(
      structure,
      input,
      item,
      diagnostics,
      groupDisplayMoney
    ),
  };
}

function calculateGroupThresholdComponents(
  thresholds: GroupThresholdInput[],
  travellerCounts: GroupTravellerCounts,
  input: PricingInput,
  item: NormalizedItemInput,
  diagnostics: PricingDiagnostic[]
): MoneyCalculation[] {
  const calculations: MoneyCalculation[] = [];

  // One chain per traveller CATEGORY: rows selecting the same category — by
  // collapsed type key or by legacy traveler-type uuid — band together.
  const chains = new Map<string | null, GroupThresholdInput[]>();
  for (const threshold of thresholds) {
    const key = resolveGroupCategoryKey(
      threshold.travellerId ?? null,
      travellerCounts,
      input
    );
    const chain = chains.get(key);
    if (chain) {
      chain.push(threshold);
    } else {
      chains.set(key, [threshold]);
    }
  }

  const uncovered: Array<{ label: string; missing: number; count: number }> =
    [];
  for (const [key, chain] of chains) {
    const chainCount =
      key === null
        ? travellerCounts.total
        : (travellerCounts.byTravellerId.get(key) ?? 0);
    if (chainCount === 0) continue;
    // Matched tier: the band whose [Min, Max] contains the chain's head count
    // prices EVERY traveller of the chain at its rate (T-198). Overlapping
    // bands are bad data; the lowest Min wins.
    const matched = [...chain]
      .sort((left, right) => left.minTravellers - right.minTravellers)
      .find((row) => groupBandRange(chainCount, row)?.upper === chainCount);
    if (!matched) {
      uncovered.push({
        label: resolveGroupCategoryLabel(key, input),
        missing: chainCount,
        count: chainCount,
      });
      continue;
    }
    pushGroupBandCalculations({
      row: { ...matched, minTravellers: 1, maxTravellers: null },
      chainKey: key,
      chainCount,
      travellerCounts,
      input,
      item,
      diagnostics,
      calculations,
    });
  }

  if (uncovered.length > 0) {
    const shortfall = uncovered
      .map((chain) => `${chain.label}: ${chain.missing} of ${chain.count}`)
      .join('; ');
    diagnostics.push(
      createDiagnostic({
        category: 'legacy_compatible_warning',
        severity: 'warning',
        code: 'GROUP_THRESHOLD_NO_MATCH',
        message: `Group price tiers do not cover every traveller (${shortfall} uncovered). Extend the Min/Max People ranges so every traveller falls inside a tier.`,
        itemId: item.id,
        blocksPersistence: false,
      })
    );
  }

  return calculations;
}

// The positions a band covers over a chain of `count` travellers: [Min, Max]
// intersected with [1, count]. Min is clamped to 1 (0 is bad data); a null Max
// is unbounded. `null` when the band covers nobody.
function groupBandRange(
  count: number,
  row: Pick<GroupThresholdInput, 'minTravellers' | 'maxTravellers'>
): { lower: number; upper: number } | null {
  const lower = Math.max(row.minTravellers, 1);
  if (count < lower) return null;
  const upper =
    row.maxTravellers === null ? count : Math.min(count, row.maxTravellers);
  if (upper < lower) return null;
  return { lower, upper };
}

function groupBandUnits(
  count: number,
  row: Pick<GroupThresholdInput, 'minTravellers' | 'maxTravellers'>
): number {
  const range = groupBandRange(count, row);
  return range === null ? 0 : range.upper - range.lower + 1;
}

// How many travellers of the Additional Cost's category this band covers.
//
// A band's Min/Max are positions in ITS OWN chain. Counting the additional
// category by replaying that window against the category's own head count is
// only meaningful when the two are different populations; when the band lives
// in the **All** chain the category is a SUBSET of it, so the window has to be
// projected onto the slice that category occupies. Replaying it instead
// silently dropped the charge whenever the category was smaller than the
// band's Min (All 3–4 + "Children" with 2 children charged nothing at all).
function groupBandAdditionalUnits({
  row,
  chainKey,
  chainCount,
  travellerCounts,
  input,
}: {
  row: GroupThresholdInput;
  chainKey: string | null;
  chainCount: number;
  travellerCounts: GroupTravellerCounts;
  input: PricingInput;
}): number {
  // No selector: the additional follows the band's own category, so it is paid
  // by exactly the people the band covers.
  if (!row.additionalTravellerId) return groupBandUnits(chainCount, row);

  const selectorKey = resolveGroupCategoryKey(
    row.additionalTravellerId,
    travellerCounts,
    input
  );
  // Selector "All", or the band's own category re-selected: same population as
  // the chain — again exactly the people the band covers.
  if (selectorKey === null || selectorKey === chainKey) {
    return groupBandUnits(chainCount, row);
  }

  const selectorCount = travellerCounts.byTravellerId.get(selectorKey) ?? 0;

  // A typed chain (Adults) and a different selector (Children) are DISJOINT
  // populations: the band's ordinal window applies to the selector's own chain,
  // which partitions those travellers across the chain's bands — each charged
  // exactly once, the unbounded last band absorbing the remainder.
  if (chainKey !== null) return groupBandUnits(selectorCount, row);

  // All chain: intersect the band's slice of the chain with the slice this
  // category occupies. Categories are laid out in the item's traveller order
  // (the order the row shows them), so 2 Adults + 2 Children put Adults at
  // positions 1–2 and Children at 3–4.
  const band = groupBandRange(chainCount, row);
  if (band === null || selectorCount === 0) return 0;
  const offset = groupCategoryOffset(selectorKey, travellerCounts);
  if (offset === null) return 0;
  const categoryLower = offset + 1;
  const categoryUpper = offset + selectorCount;
  const lower = Math.max(band.lower, categoryLower);
  const upper = Math.min(band.upper, categoryUpper);
  return upper < lower ? 0 : upper - lower + 1;
}

// Position (0-based) of a category's first traveller inside the All chain.
function groupCategoryOffset(
  key: string,
  counts: GroupTravellerCounts
): number | null {
  let offset = 0;
  for (const [categoryKey, quantity] of counts.byTravellerId) {
    if (categoryKey === key) return offset;
    offset = add(offset, quantity);
  }
  return null;
}

function resolveGroupCategoryLabel(
  key: string | null,
  input: PricingInput
): string {
  if (key === null) return 'All';
  const traveller = input.travellers.find(
    (candidate) =>
      candidate.id === key ||
      candidate.sourceId === key ||
      candidate.type === key
  );
  if (traveller?.label) return traveller.label;
  // A chain key IS the collapsed type ('adult'), so fall back to formatting it
  // directly rather than the generic 'Traveller' — the warning has to name the
  // category the user must fix.
  const type =
    traveller?.type ??
    input.travellerQuantities.find((quantity) => quantity.travellerId === key)
      ?.type ??
    (key as TravellerInput['type']);
  return formatTravellerType(type);
}

function pushGroupBandCalculations({
  row,
  chainKey,
  chainCount,
  travellerCounts,
  input,
  item,
  diagnostics,
  calculations,
}: {
  row: GroupThresholdInput;
  chainKey: string | null;
  chainCount: number;
  travellerCounts: GroupTravellerCounts;
  input: PricingInput;
  item: NormalizedItemInput;
  diagnostics: PricingDiagnostic[];
  calculations: MoneyCalculation[];
}): { lower: number; upper: number } | null {
  const coveredRange = groupBandRange(chainCount, row);
  const covered = coveredRange
    ? coveredRange.upper - coveredRange.lower + 1
    : 0;

  // A band carries ONE price; which slot holds it is a mapping artifact (the
  // adapter routes child-type rows into childPrice, the editor always writes
  // adultPrice). Both slots set is the legacy adult/child split — each kind
  // pays its own price over its own positions.
  if (row.adultPrice !== null && row.childPrice !== null) {
    pushGroupCalculation(
      calculations,
      row.adultPrice,
      groupBandUnits(travellerCounts.adult, row),
      input,
      item,
      diagnostics
    );
    pushGroupCalculation(
      calculations,
      row.childPrice,
      groupBandUnits(travellerCounts.child, row),
      input,
      item,
      diagnostics
    );
  } else {
    pushGroupCalculation(
      calculations,
      row.adultPrice ?? row.childPrice,
      covered,
      input,
      item,
      diagnostics
    );
  }

  // Additional Cost: paid once per covered person of its category — see
  // `groupBandAdditionalUnits` for how a selector is projected onto the band.
  // Both slots set is again the legacy adult/child split.
  if (row.additionalAdultPrice !== null && row.additionalChildPrice !== null) {
    pushGroupCalculation(
      calculations,
      row.additionalAdultPrice,
      groupBandUnits(travellerCounts.adult, row),
      input,
      item,
      diagnostics
    );
    pushGroupCalculation(
      calculations,
      row.additionalChildPrice,
      groupBandUnits(travellerCounts.child, row),
      input,
      item,
      diagnostics
    );
  } else {
    const additionalComponent =
      row.additionalAdultPrice ?? row.additionalChildPrice;
    if (additionalComponent) {
      pushGroupCalculation(
        calculations,
        additionalComponent,
        groupBandAdditionalUnits({
          row,
          chainKey,
          chainCount,
          travellerCounts,
          input,
        }),
        input,
        item,
        diagnostics
      );
    }
  }

  return coveredRange;
}

// A threshold's travellerId may be a collapsed TYPE key ('adult'), a legacy
// traveler-type uuid (V2 rows), or null (All). Group traveller counts are keyed
// by TYPE (collapseTravellersByType), so a uuid resolves through the itinerary
// traveller lookup to its type — and uuid- and type-keyed rows for the same
// category land in the same chain.
function resolveGroupCategoryKey(
  travellerId: string | null,
  counts: GroupTravellerCounts,
  input: PricingInput
): string | null {
  if (!travellerId) return null;
  if (counts.byTravellerId.has(travellerId)) return travellerId;
  const resolvedType =
    input.travellers.find((traveller) => traveller.id === travellerId)?.type ??
    input.travellerQuantities.find(
      (quantity) => quantity.travellerId === travellerId
    )?.type ??
    null;
  if (resolvedType && counts.byTravellerId.has(resolvedType)) {
    return resolvedType;
  }
  return resolvedType ?? travellerId;
}

function pushGroupCalculation(
  calculations: MoneyCalculation[],
  component: NormalizedPriceComponent | null,
  units: number,
  input: PricingInput,
  item: NormalizedItemInput,
  diagnostics: PricingDiagnostic[]
): void {
  if (!component || units === 0) return;
  calculations.push(
    calculateMoney(component, units, 1, input, item, diagnostics)
  );
}

function buildPersistenceGroupStructure(
  structure: GroupPriceStructure,
  input: PricingInput,
  item: NormalizedItemInput,
  diagnostics: PricingDiagnostic[],
  // The row money computed against the TIER cost basis. Persisting that (rather
  // than re-running the flat line against its own cost, which is 0 for a normal
  // GROUP) is what makes markup / a typed price round-trip: the save writes the
  // group's `price` as null, so the reload has to rebuild the selling side from
  // the markup, and a markup derived against cost 0 is always 0.
  priceLineMoney?: CalculatedMoneyFields
): PersistenceSafeCalculatedStructure {
  return {
    kind: 'group',
    unit: 'GROUP',
    fallbackPolicy: structure.fallbackPolicy,
    cost: structure.cost
      ? {
          ...(priceLineMoney ??
            calculateMoney(structure.cost, 1, 1, input, item, diagnostics)
              .money),
          // Preserve the RAW commission % (and gross flag): `calculateMoney`
          // re-derives it from the commission AMOUNT, which is 0 whenever the
          // cost basis is 0, so the group's commission would be silently
          // dropped on save/reload.
          commissionPercent: structure.cost.commissionPercent ?? null,
          // Same for the markup, but ONLY when it is the driver and the derived
          // value would be meaningless (zero cost basis). With a real cost basis
          // the derived percent above is authoritative — it is how a typed price
          // survives a save that cannot persist the price itself.
          ...(!priceLineMoney?.cost
            ? { markupPercent: structure.cost.markupPercent ?? null }
            : {}),
          isGross: structure.cost.isGross,
        }
      : null,
    travellers: structure.travellers,
    thresholds: structure.thresholds.map((threshold) => ({
      thresholdId: threshold.id ?? null,
      sourceId: threshold.sourceId ?? null,
      travellerId: threshold.travellerId ?? null,
      additionalTravellerId: threshold.additionalTravellerId ?? null,
      travellerLabel: threshold.travellerId
        ? resolveTravellerDisplayLabel(input, threshold.travellerId, 'unknown')
        : null,
      additionalTravellerLabel: threshold.additionalTravellerId
        ? resolveTravellerDisplayLabel(
            input,
            threshold.additionalTravellerId,
            'unknown'
          )
        : null,
      minTravellers: threshold.minTravellers,
      maxTravellers: threshold.maxTravellers,
      adultPrice: threshold.adultPrice
        ? calculateMoney(threshold.adultPrice, 1, 1, input, item, diagnostics)
            .money
        : null,
      childPrice: threshold.childPrice
        ? calculateMoney(threshold.childPrice, 1, 1, input, item, diagnostics)
            .money
        : null,
      additionalAdultPrice: threshold.additionalAdultPrice
        ? calculateMoney(
            threshold.additionalAdultPrice,
            1,
            1,
            input,
            item,
            diagnostics
          ).money
        : null,
      additionalChildPrice: threshold.additionalChildPrice
        ? calculateMoney(
            threshold.additionalChildPrice,
            1,
            1,
            input,
            item,
            diagnostics
          ).money
        : null,
    })),
  };
}

function getGroupTravellerCountsFromList(
  travellers: ReadonlyArray<{
    travellerId: string;
    type: TravellerInput['type'];
    quantity: number;
  }>
): GroupTravellerCounts {
  return travellers.reduce(
    (counts, traveller) => {
      counts.byTravellerId.set(
        traveller.travellerId,
        add(
          counts.byTravellerId.get(traveller.travellerId) ?? 0,
          traveller.quantity
        )
      );
      counts.total = add(counts.total, traveller.quantity);
      if (traveller.type === 'adult' || traveller.type === 'senior') {
        counts.adult = add(counts.adult, traveller.quantity);
      } else {
        counts.child = add(counts.child, traveller.quantity);
      }
      return counts;
    },
    { adult: 0, child: 0, total: 0, byTravellerId: new Map<string, number>() }
  );
}

function calculateMoney(
  component: NormalizedPriceComponent,
  units: number,
  unitsMultiplier: number,
  input: PricingInput,
  item: NormalizedItemInput,
  diagnostics: PricingDiagnostic[]
): MoneyCalculation {
  const rate = resolveExchangeRate(
    component.currency,
    input.context.baseCurrency,
    input,
    diagnostics,
    item.id
  );
  const effectiveUnits = multiply(units, unitsMultiplier);
  const cost = safeNumber(component.cost);
  const costExclTax = excludeVatFromAmount(cost, component.costVat);
  const netCost = resolveNetCost(component, costExclTax);
  const commissionAmount = Math.max(0, subtract(costExclTax, netCost));
  // When the commission percent drives netCost (no explicit netCost), echo it
  // exactly — re-deriving it from the 4dp commission amount would replace the
  // typed percent with a near-miss (12.34 → 12.33693369).
  const commissionPercent =
    component.netCost === null && component.commissionPercent !== null
      ? Math.max(0, component.commissionPercent)
      : Math.max(0, percentage(commissionAmount, costExclTax));
  const rawUnitPrice = resolveRawUnitPrice(
    component,
    cost,
    effectiveUnits,
    rate,
    commissionAmount
  );
  const markupAmount = cost === 0 ? 0 : subtract(rawUnitPrice, cost);
  // Same for markup: when it drives the price (no explicit unit/final price
  // or profit), the typed percent is the source of truth and must survive
  // calculation and persistence verbatim.
  const markupIsDriver =
    component.unitPrice === null &&
    component.finalPrice === null &&
    component.profit === null;
  const markupPercent =
    cost !== 0 && markupIsDriver && component.markupPercent !== null
      ? component.markupPercent
      : percentage(markupAmount, cost);
  const finalCost = divide(multiply(cost, effectiveUnits), rate);
  // Net owed to the supplier for the whole line. Stays in the SOURCE currency
  // (no `rate` divide) because `netCost` is unconverted too — the pair is what
  // the supplier invoices, and a reservation email quotes them their own money.
  const finalNetCost = multiply(netCost, effectiveUnits);
  const convertedUnitPrice = resolveConvertedUnitPrice(
    component,
    rawUnitPrice,
    rate
  );
  const finalPrice = multiply(convertedUnitPrice, effectiveUnits);
  const finalPriceWithTax = includeVatInAmount(finalPrice, component.priceVat);
  const finalCommissionAmount = multiply(
    divide(commissionAmount, rate),
    effectiveUnits
  );
  const profit = normalizeZero(
    add(subtract(finalPrice, finalCost), finalCommissionAmount)
  );

  return {
    money: {
      currency: input.context.baseCurrency,
      sourceCurrency: component.currency,
      cost,
      netCost,
      finalNetCost,
      unitPrice: convertedUnitPrice,
      sourceUnitPrice: rawUnitPrice,
      finalPrice,
      finalPriceWithTax,
      profit,
      markupPercent,
      commissionPercent,
      units,
      costVat: component.costVat ?? ZERO_VAT,
      priceVat: component.priceVat ?? ZERO_VAT,
      isGross: component.isGross,
      useNetCost: component.useNetCost,
      rate,
      finalCost,
    },
    commissionAmount,
    finalCommissionAmount,
    finalCost,
    sourceCurrency: component.currency,
    rate,
  };
}

// The base-currency price shown in the Pricing Table. Dividing the item-currency
// figure back out loses the typed value, because persistence quantizes that
// figure to 2dp: 120 typed at rate 0.7452 stores 89.42 and reloads as 119.99
// (XT-545). When the stored provenance still reproduces the stored price under
// the current rate, echo it verbatim instead.
//
// The guard is what keeps this honest — if the org edited the exchange rate
// after the save, it fails and the price legitimately re-derives from the rate.
function resolveConvertedUnitPrice(
  component: NormalizedPriceComponent,
  rawUnitPrice: number,
  rate: number
): number {
  const baseUnitPrice = component.baseUnitPrice;
  if (
    baseUnitPrice !== null &&
    baseUnitPrice !== undefined &&
    roundMoneyAmount(multiply(baseUnitPrice, rate)) ===
      roundMoneyAmount(rawUnitPrice)
  ) {
    return baseUnitPrice;
  }

  return divide(rawUnitPrice, rate);
}

function resolveRawUnitPrice(
  component: NormalizedPriceComponent,
  cost: number,
  effectiveUnits: number,
  rate: number,
  commissionAmount: number
): number {
  if (component.unitPrice !== null) return component.unitPrice;

  if (component.finalPrice !== null && effectiveUnits !== 0) {
    return multiply(divide(component.finalPrice, effectiveUnits), rate);
  }

  if (component.profit !== null && effectiveUnits !== 0) {
    const finalCost = divide(multiply(cost, effectiveUnits), rate);
    const finalCommissionAmount = multiply(
      divide(commissionAmount, rate),
      effectiveUnits
    );
    const finalPrice = add(
      subtract(component.profit, finalCommissionAmount),
      finalCost
    );
    return multiply(divide(finalPrice, effectiveUnits), rate);
  }

  if (component.markupPercent !== null) {
    return add(cost, multiply(cost, percentToRatio(component.markupPercent)));
  }

  return 0;
}

function resolveNetCost(
  component: NormalizedPriceComponent,
  costExclTax: number
): number {
  if (component.netCost !== null) return component.netCost;
  if (component.commissionPercent !== null) {
    return subtract(
      costExclTax,
      multiply(costExclTax, percentToRatio(component.commissionPercent))
    );
  }
  return costExclTax;
}

function resolveExchangeRate(
  sourceCurrency: CurrencyCode,
  targetCurrency: CurrencyCode,
  input: PricingInput,
  diagnostics: PricingDiagnostic[],
  itemId: string
): number {
  const resolved = findExchangeRate(sourceCurrency, targetCurrency, input);
  if (resolved !== null) return resolved;

  diagnostics.push(
    createDiagnostic({
      category: 'currency_fallback_warning',
      severity: 'warning',
      code: 'EXCHANGE_RATE_FALLBACK_ONE',
      message: `Missing exchange rate ${sourceCurrency} to ${targetCurrency}; using one.`,
      itemId,
      blocksPersistence: false,
    })
  );
  return 1;
}

function findExchangeRate(
  sourceCurrency: CurrencyCode,
  targetCurrency: CurrencyCode,
  input: PricingInput
): number | null {
  if (sourceCurrency === targetCurrency) return 1;

  // Callers convert source→target by DIVIDING the amount by this rate, so the
  // value must be "source units per 1 target unit". That magnitude is the
  // record stored target→source (e.g. converting USD→EUR wants EUR→USD =
  // USD-per-EUR). Prefer it: a full bidirectional rate matrix also stores the
  // direct source→target record (the reciprocal), and using that with the
  // divide would invert the result (100 USD ÷ 0.86 = 116 instead of ÷ 1.16 =
  // 86). The legacy-inverse-reset flag (always on for itinerary pricing) gates
  // this, matching legacy's behaviour of consuming the inverse rate as-is.
  const inverse = input.exchangeRates.rates.find(
    (rate) =>
      rate.sourceCurrency === targetCurrency &&
      rate.targetCurrency === sourceCurrency
  );
  if (inverse && input.exchangeRates.preserveLegacyInverseResetBehavior) {
    return normalizeRate(inverse.rate);
  }

  const direct = input.exchangeRates.rates.find(
    (rate) =>
      rate.sourceCurrency === sourceCurrency &&
      rate.targetCurrency === targetCurrency
  );
  if (direct) return normalizeRate(direct.rate);

  const sourceRate = findBaseRate(sourceCurrency, input.exchangeRates.rates);
  const targetRate = findBaseRate(targetCurrency, input.exchangeRates.rates);
  if (sourceRate !== null && targetRate !== null && sourceRate !== 0) {
    return normalizeRate(sourceRate / targetRate);
  }

  return null;
}

function lookupExchangeRate(
  sourceCurrency: CurrencyCode,
  targetCurrency: CurrencyCode,
  input: PricingInput
): number {
  return findExchangeRate(sourceCurrency, targetCurrency, input) ?? 1;
}

function normalizeRate(rate: number): number {
  return rate === 0 ? 1 : rate;
}

function findBaseRate(
  currency: CurrencyCode,
  rates: ExchangeRateRecord[]
): number | null {
  const rate = rates.find(
    (candidate) =>
      candidate.sourceCurrency === currency ||
      candidate.targetCurrency === currency ||
      candidate.baseCurrency === currency
  );
  if (!rate) return null;
  if (rate.sourceCurrency === currency) return rate.rate;
  if (rate.targetCurrency === currency && rate.rate !== 0) return 1 / rate.rate;
  if (rate.baseCurrency === currency) return 1;
  return null;
}

// Undated itineraries anchor every timestamp on the year-2000 sentinel (same
// signal as `isUndatedTimestamp` in itineraries day-meta + the preview). The
// pricing table then labels rows/sections as "Day N" instead of the
// meaningless "Jan 01, 2000", mirroring the planner.
const isUndatedInstant = (date: Date | null): boolean =>
  date?.getUTCFullYear() === 2000;

function formatPricingRowDateLabel({
  endTime,
  itineraryStartTime,
  startTime,
}: {
  endTime: string | null;
  itineraryStartTime: string | null;
  startTime: string | null;
}): string | null {
  const start = parseDateValue(startTime);
  if (!start) return null;

  const end = parseDateValue(endTime);
  const itineraryStart = parseDateValue(itineraryStartTime);
  const dayIndex = itineraryStart
    ? differenceInUtcCalendarDays(start, itineraryStart) + 1
    : null;
  // No day prefix without an itinerary-start context (dayIndex null). Undated
  // itineraries always carry the year-2000 sentinel start, so they still get it.
  const dayPrefix =
    dayIndex !== null && dayIndex > 0 ? `Day ${dayIndex} - ` : '';

  // V2 parity (`formatDateForPricingTable` in travel_planner_front/lib/utils.ts):
  //   "Day N - {start} — {end}[ + X days]"
  // Undated itineraries (year-2000 sentinel) show the TIME only ("Day 1 - 08:50
  // — 17:59"); dated itineraries show the full date + time. Never the raw
  // sentinel "Jan 01, 2000". The "+ X days" suffix marks a multi-day span
  // (accommodation / rental).
  const startLabel =
    isUndatedInstant(itineraryStart) || isUndatedInstant(start)
      ? formatTime(start)
      : `${formatLongDate(start)}, ${formatWeekday(start)}, ${formatTime(start)}`;
  const endLabel = end ? ` — ${formatTime(end)}` : '';
  const dayDiff = end ? differenceInUtcCalendarDays(end, start) : 0;
  const daySuffix =
    dayDiff > 0 ? ` + ${dayDiff} day${dayDiff === 1 ? '' : 's'}` : '';

  return `${dayPrefix}${startLabel}${endLabel}${daySuffix}`;
}

// An item dated before the itinerary start is bucketed onto day 1 in the
// planner (`computeDayIndex` clamps a negative day offset to 0, and day 1
// carries the itinerary start date). Anchor the period label on the itinerary
// start in that case so the modal/table never show a check-in date earlier than
// the planner displays (e.g. label "21 Apr" while the planner shows "23 Apr").
function clampStartToItineraryStart(
  itemStartDate: string | null,
  itineraryStartDate: string | null
): string | null {
  const itemStart = parseDateValue(itemStartDate);
  const itineraryStart = parseDateValue(itineraryStartDate);
  if (!itemStart || !itineraryStart) return itemStartDate;
  const itemDay = Date.UTC(
    itemStart.getUTCFullYear(),
    itemStart.getUTCMonth(),
    itemStart.getUTCDate()
  );
  const itineraryDay = Date.UTC(
    itineraryStart.getUTCFullYear(),
    itineraryStart.getUTCMonth(),
    itineraryStart.getUTCDate()
  );
  return itemDay < itineraryDay ? itineraryStartDate : itemStartDate;
}

function formatRoomPeriodDisplayLabel({
  itemStartDate,
  period,
  periodNightCount,
}: {
  itemStartDate: string | null;
  period: RoomPricePeriod;
  periodNightCount: number;
}): string {
  const dateRange =
    formatShortDateRange(period.startDate, period.endDate) ??
    deriveRoomPeriodDateRange({
      itemStartDate,
      nightCount: periodNightCount,
      startNight: period.startNight,
    });
  const startNight = period.startNight ?? 1;
  const nightLabel = `night ${startNight} · ${periodNightCount} night${
    periodNightCount === 1 ? '' : 's'
  }`;

  return [dateRange, nightLabel].filter(Boolean).join(' · ');
}

function deriveRoomPeriodDateRange({
  itemStartDate,
  nightCount,
  startNight,
}: {
  itemStartDate: string | null;
  nightCount: number;
  startNight: number | null;
}): string | null {
  const itemStart = parseDateValue(itemStartDate);
  if (!itemStart) return null;

  const zeroBasedStartNight = Math.max((startNight ?? 1) - 1, 0);
  const startDate = addUtcDays(itemStart, zeroBasedStartNight);
  const endDate = addUtcDays(startDate, nightCount);

  return formatShortDateRangeFromDates(startDate, endDate);
}

function formatShortDateRange(
  startDate: string | null,
  endDate: string | null
): string | null {
  const start = parseDateValue(startDate);
  const end = parseDateValue(endDate);
  if (!start && !end) return null;
  if (start && end) return formatShortDateRangeFromDates(start, end);
  return formatShortDate(start ?? end);
}

function formatShortDateRangeFromDates(start: Date, end: Date): string {
  return `${formatShortDate(start)} - ${formatShortDate(end)}`;
}

function parseDateValue(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function addUtcDays(date: Date, days: number): Date {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + days,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    )
  );
}

function differenceInUtcCalendarDays(left: Date, right: Date): number {
  const leftDay = Date.UTC(
    left.getUTCFullYear(),
    left.getUTCMonth(),
    left.getUTCDate()
  );
  const rightDay = Date.UTC(
    right.getUTCFullYear(),
    right.getUTCMonth(),
    right.getUTCDate()
  );

  return Math.round((leftDay - rightDay) / 86_400_000);
}

function formatLongDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(date);
}

function formatShortDate(date: Date | null): string | null {
  if (!date) return null;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  }).format(date);
}

function formatWeekday(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(date);
}

function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

function resolveTravellerDisplayLabel(
  input: PricingInput,
  travellerId: string,
  fallbackType: TravellerInput['type']
): string {
  const traveller = input.travellers.find(
    (candidate) =>
      candidate.id === travellerId || candidate.sourceId === travellerId
  );

  return (
    traveller?.label ?? formatTravellerType(traveller?.type ?? fallbackType)
  );
}

function formatTravellerType(type: TravellerInput['type']): string {
  switch (type) {
    case 'adult':
      return 'Adult';
    case 'child':
      return 'Child';
    case 'teen':
      return 'Teen';
    case 'baby':
      return 'Baby';
    case 'senior':
      return 'Senior';
    default:
      return 'Traveller';
  }
}

function buildPricingTableOutput(
  input: PricingInput,
  itemTotals: ItemTotal[],
  diagnostics: PricingDiagnostic[]
): PricingTableOutput {
  const itemsById = new Map(input.items.map((item) => [item.id, item]));
  const diagnosticsByItemId = groupDiagnosticsByItemId(diagnostics);

  // Rows intentionally include every calculated item; consumers use
  // inclusion.pricingTableDisplay for visibility so hidden restore/save flows
  // retain calculated data.
  const rows: PricingTableRow[] = itemTotals.map((total) => {
    const rowDiagnostics = diagnosticsByItemId.get(total.item.id) ?? [];

    return {
      id: total.item.id,
      itemId: total.item.id,
      parentId: total.item.parentId,
      title: total.item.title,
      priceStructureKind: total.item.priceStructure.kind,
      unit: getItemUnit(total.item.priceStructure),
      inclusion: total.inclusion,
      flags: total.item.flags,
      quantity: total.quantity,
      money: total.money,
      diagnostics: rowDiagnostics,
      display: buildPricingTableRowDisplay(
        total.item,
        itemsById,
        input.context.itineraryStartTime ?? null
      ),
      displayMetadata: total.item.sourceMetadata,
    };
  });

  // Stages 3-4 of the pipeline: the adjustment blocks are evaluated once, from
  // the aggregates of the rows the pricing table displays a total for, and the
  // resulting subtotals are folded into every totals scope. A quoted Surcharge
  // / Agency Commission line is a concrete figure — recomputing it per scope
  // would print one number on screen and a different one on the quote.
  const adjustments = buildPricingAdjustments(input, itemTotals, diagnostics);
  const pricingTableTotal = addAdjustmentsToTotals(
    totalForScope(itemTotals, 'pricingTableTotal', input),
    adjustments
  );
  const versionPrice = addAdjustmentsToTotals(
    totalForScope(itemTotals, 'versionPrice', input),
    adjustments
  );
  const exportTotal = addAdjustmentsToTotals(
    totalForScope(itemTotals, 'export', input),
    adjustments
  );

  const sections = buildPricingTableSections(
    input,
    itemTotals,
    diagnosticsByItemId
  );
  const daySections = buildPricingTableDaySections(
    input,
    itemTotals,
    diagnosticsByItemId
  );

  return {
    baseCurrency: input.context.baseCurrency,
    rows,
    sections,
    ...(daySections ? { daySections } : {}),
    adjustments,
    totals: {
      pricingTableTotal,
      versionPrice,
      export: exportTotal,
    },
    persistenceSafe: buildPersistenceSafeFields(
      input,
      itemTotals,
      versionPrice
    ),
  };
}

function buildPricingTableDaySections(
  input: PricingInput,
  itemTotals: ItemTotal[],
  diagnosticsByItemId: ReadonlyMap<string, PricingDiagnostic[]>
): PricingTableSection[] | undefined {
  const itineraryStartTime = input.context.itineraryStartTime ?? null;
  const itineraryStart = parseDateValue(itineraryStartTime);

  if (!itineraryStart) {
    return undefined;
  }

  const sectionsById = new Map<string, PricingTableSectionDraft>();

  for (const total of itemTotals) {
    const day = resolvePricingTableDisplayDay(
      total.item,
      itineraryStart,
      input.context.dayTitles
    );
    const rowDiagnostics = diagnosticsByItemId.get(total.item.id) ?? [];
    const existing = sectionsById.get(day.sectionId);
    const section =
      existing ??
      ({
        id: day.sectionId,
        categoryKey: day.key,
        categoryLabel: day.label,
        ...(day.dayIndex !== null ? { dayIndex: day.dayIndex } : {}),
        ...(day.dayTitle ? { dayTitle: day.dayTitle } : {}),
        rowIds: [],
        rowCounts: {
          total: 0,
          visible: 0,
          optional: 0,
          hidden: 0,
          excluded: 0,
          lineItems: 0,
        },
        diagnosticCounts: {
          total: 0,
          blocking: 0,
        },
        itemTotals: [],
        sortOrder: day.sortOrder,
      } satisfies PricingTableSectionDraft);

    section.rowIds.push(total.item.id);
    section.itemTotals.push(total);
    section.rowCounts.total += 1;
    if (total.inclusion.pricingTableDisplay) section.rowCounts.visible += 1;
    if (total.item.flags.isOptional) section.rowCounts.optional += 1;
    if (total.item.flags.isHidden) section.rowCounts.hidden += 1;
    if (total.item.flags.isExcluded || total.item.flags.isDeleted) {
      section.rowCounts.excluded += 1;
    }
    if (total.item.flags.isLine || total.item.flags.isManualLine) {
      section.rowCounts.lineItems += 1;
    }
    section.diagnosticCounts.total += rowDiagnostics.length;
    section.diagnosticCounts.blocking += rowDiagnostics.filter(
      (diagnostic) => diagnostic.blocksPersistence
    ).length;

    if (!existing) {
      sectionsById.set(day.sectionId, section);
    }
  }

  return [...sectionsById.values()]
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
      }
      return left.categoryLabel.localeCompare(right.categoryLabel);
    })
    .map((section, displayOrder) => ({
      id: section.id,
      categoryKey: section.categoryKey,
      categoryLabel: section.categoryLabel,
      ...(section.dayIndex !== undefined ? { dayIndex: section.dayIndex } : {}),
      ...(section.dayTitle !== undefined ? { dayTitle: section.dayTitle } : {}),
      rowIds: orderSectionRowIds(section.itemTotals, { nestParentChild: true }),
      displayOrder,
      totals: totalForScope(section.itemTotals, 'pricingTableTotal', input),
      rowCounts: section.rowCounts,
      diagnosticCounts: section.diagnosticCounts,
    }));
}

// Manual / added price lines (`isLine`) always render at the END of their
// section — legacy `GroupTable` rendered `group.lines` after `group.items`.
// `nestParentChild` additionally nests subitems directly under their parent
// (Day grouping — see orderRowIdsByParentChild). Relative order is otherwise
// preserved.
function orderSectionRowIds(
  itemTotals: ItemTotal[],
  options: { nestParentChild: boolean }
): string[] {
  const lines: ItemTotal[] = [];
  const nonLines: ItemTotal[] = [];
  for (const total of itemTotals) {
    (total.item.flags.isLine ? lines : nonLines).push(total);
  }
  const nonLineIds = options.nestParentChild
    ? orderRowIdsByParentChild(nonLines)
    : nonLines.map((total) => total.item.id);
  return [...nonLineIds, ...lines.map((total) => total.item.id)];
}

// Day grouping collects items by date in start-time order, which can place a
// subitem before its parent (a subitem often starts at/before the parent, and
// `asc(startTime), asc(id)` from the read breaks ties by id). The pricing table
// renders subitems nested under their parent, so order each day's rows
// parent-first with descendants immediately after — legacy `group-pricing-rows`
// nested subitems under the parent. Relative order is otherwise preserved;
// subitems whose parent falls in another day stay as top-level rows of their
// own day.
function orderRowIdsByParentChild(itemTotals: ItemTotal[]): string[] {
  const idsInSection = new Set(itemTotals.map((total) => total.item.id));
  const childrenByParent = new Map<string, ItemTotal[]>();
  const roots: ItemTotal[] = [];
  for (const total of itemTotals) {
    const parentId = total.item.parentId;
    if (parentId && idsInSection.has(parentId)) {
      const siblings = childrenByParent.get(parentId) ?? [];
      siblings.push(total);
      childrenByParent.set(parentId, siblings);
    } else {
      roots.push(total);
    }
  }

  const ordered: string[] = [];
  const visited = new Set<string>();
  const emit = (total: ItemTotal): void => {
    if (visited.has(total.item.id)) return;
    visited.add(total.item.id);
    ordered.push(total.item.id);
    for (const child of childrenByParent.get(total.item.id) ?? []) emit(child);
  };
  for (const root of roots) emit(root);
  // Safety net: a parent-child cycle would leave items unvisited — append them
  // in their original (start-time) order so every row still renders once.
  for (const total of itemTotals) emit(total);
  return ordered;
}

function resolvePricingTableDisplayDay(
  item: NormalizedItemInput,
  itineraryStart: Date | null,
  dayTitles?: ReadonlyArray<string | null> | null
): {
  key: string;
  label: string;
  sectionId: string;
  sortOrder: number;
  dayIndex: number | null;
  dayTitle: string | null;
} {
  const itemStart = parseDateValue(
    item.startTime ?? item.dateRange?.startDate ?? null
  );

  if (!itemStart) {
    return {
      key: 'UNKNOWN_DAY',
      label: 'Other',
      sectionId: 'pricing-day:UNKNOWN_DAY',
      sortOrder: Number.MAX_SAFE_INTEGER,
      dayIndex: null,
      dayTitle: null,
    };
  }

  const dayIndex = itineraryStart
    ? differenceInUtcCalendarDays(itemStart, itineraryStart) + 1
    : 1;
  const safeDayIndex = dayIndex > 0 ? dayIndex : 1;
  const dateLabel = formatLongDate(itemStart);
  const key = `DAY_${safeDayIndex}`;
  const customTitle = dayTitles?.[safeDayIndex - 1]?.trim() || null;
  // Undated itineraries show "Day N" alone, never the sentinel "Jan 01, 2000".
  // A stored (custom or AI) day title is appended after the date, never in
  // place of it, so the service date stays visible.
  const dayLabel =
    isUndatedInstant(itemStart) || isUndatedInstant(itineraryStart)
      ? `Day ${safeDayIndex}`
      : `Day ${safeDayIndex} - ${dateLabel}`;

  return {
    key,
    label: customTitle ? `${dayLabel} - ${customTitle}` : dayLabel,
    sectionId: `pricing-day:${key}`,
    sortOrder: safeDayIndex,
    dayIndex: safeDayIndex,
    dayTitle: customTitle,
  };
}

function buildPricingTableRowDisplay(
  item: NormalizedItemInput,
  itemsById: ReadonlyMap<string, NormalizedItemInput>,
  itineraryStartTime: string | null
): PricingTableRowDisplay {
  const category = resolvePricingTableDisplayCategory(item.contentCategory);
  const subcategory = resolvePricingTableDisplaySubcategory(
    item.contentSubcategory
  );

  return {
    sectionId: category.sectionId,
    categoryKey: category.key,
    categoryLabel: category.label,
    subcategoryKey: subcategory.key,
    subcategoryLabel: subcategory.label,
    startTime: item.startTime ?? null,
    endTime: item.dateRange?.endDate ?? null,
    startDate: item.dateRange?.startDate ?? null,
    endDate: item.dateRange?.endDate ?? null,
    dateLabel: formatPricingRowDateLabel({
      endTime: item.dateRange?.endDate ?? null,
      itineraryStartTime,
      startTime: item.startTime ?? item.dateRange?.startDate ?? null,
    }),
    depth: resolveItemDepth(item, itemsById),
    childIds: item.childIds,
  };
}

function buildPricingTableSections(
  input: PricingInput,
  itemTotals: ItemTotal[],
  diagnosticsByItemId: ReadonlyMap<string, PricingDiagnostic[]>
): PricingTableSection[] {
  const sectionsById = new Map<string, PricingTableSectionDraft>();
  const totalsById = new Map(itemTotals.map((total) => [total.item.id, total]));

  for (const total of itemTotals) {
    // Subitems render in their PARENT's category section — legacy GroupTable
    // grouped only top-level rows by category and nested subitems under the
    // parent, ignoring the subitem's own category for placement. Anchor the
    // section on the top-level ancestor; an orphan (parent not priced) falls
    // back to its own category and stays a top-level row.
    const anchor = resolveSectionAnchorTotal(total, totalsById);
    const category = resolvePricingTableDisplayCategory(
      anchor.item.contentCategory
    );
    const rowDiagnostics = diagnosticsByItemId.get(total.item.id) ?? [];
    const existing = sectionsById.get(category.sectionId);
    const section =
      existing ??
      ({
        id: category.sectionId,
        categoryKey: category.key,
        categoryLabel: category.label,
        rowIds: [],
        rowCounts: {
          total: 0,
          visible: 0,
          optional: 0,
          hidden: 0,
          excluded: 0,
          lineItems: 0,
        },
        diagnosticCounts: {
          total: 0,
          blocking: 0,
        },
        itemTotals: [],
        sortOrder: category.sortOrder,
      } satisfies PricingTableSectionDraft);

    section.rowIds.push(total.item.id);
    section.itemTotals.push(total);
    section.rowCounts.total += 1;
    if (total.inclusion.pricingTableDisplay) section.rowCounts.visible += 1;
    if (total.item.flags.isOptional) section.rowCounts.optional += 1;
    if (total.item.flags.isHidden) section.rowCounts.hidden += 1;
    if (total.item.flags.isExcluded || total.item.flags.isDeleted) {
      section.rowCounts.excluded += 1;
    }
    if (total.item.flags.isLine || total.item.flags.isManualLine) {
      section.rowCounts.lineItems += 1;
    }
    section.diagnosticCounts.total += rowDiagnostics.length;
    section.diagnosticCounts.blocking += rowDiagnostics.filter(
      (diagnostic) => diagnostic.blocksPersistence
    ).length;

    if (!existing) {
      sectionsById.set(category.sectionId, section);
    }
  }

  return [...sectionsById.values()]
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
      }
      return left.categoryLabel.localeCompare(right.categoryLabel);
    })
    .map((section, displayOrder) => ({
      id: section.id,
      categoryKey: section.categoryKey,
      categoryLabel: section.categoryLabel,
      rowIds: orderSectionRowIds(section.itemTotals, {
        nestParentChild: true,
      }),
      displayOrder,
      totals: totalForScope(section.itemTotals, 'pricingTableTotal', input),
      rowCounts: section.rowCounts,
      diagnosticCounts: section.diagnosticCounts,
    }));
}

// Walk up the parent chain to the top-most ancestor present in the priced
// item set; that ancestor's category decides which section the whole subtree
// renders in (legacy nested subitems under their parent, in the parent's
// category group). Cycle- and orphan-safe: a subitem whose parent isn't
// priced anchors on itself and stays a top-level row.
function resolveSectionAnchorTotal(
  total: ItemTotal,
  totalsById: ReadonlyMap<string, ItemTotal>
): ItemTotal {
  let current = total;
  const seen = new Set<string>();
  while (current.item.parentId && !seen.has(current.item.id)) {
    seen.add(current.item.id);
    const parent = totalsById.get(current.item.parentId);
    if (!parent) break;
    current = parent;
  }
  return current;
}

function groupDiagnosticsByItemId(
  diagnostics: PricingDiagnostic[]
): Map<string, PricingDiagnostic[]> {
  const diagnosticsByItemId = new Map<string, PricingDiagnostic[]>();
  const seenKeys = new Set<string>();

  for (const diagnostic of diagnostics) {
    if (!diagnostic.itemId) continue;
    // Repeated identical findings (e.g. one missing-traveller-quantity
    // warning per traveller price line on the same item) collapse to a
    // single row diagnostic. Rows, section counts and day sections all
    // read from this map, so the pricing table reports each distinct
    // message once; the raw output.diagnostics list stays untouched.
    const key = [
      diagnostic.itemId,
      diagnostic.lineId ?? '',
      diagnostic.intentId ?? '',
      diagnostic.category,
      diagnostic.severity,
      diagnostic.code,
      diagnostic.message,
      diagnostic.blocksPersistence,
    ].join('|');
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    const existing = diagnosticsByItemId.get(diagnostic.itemId) ?? [];
    diagnosticsByItemId.set(diagnostic.itemId, [...existing, diagnostic]);
  }

  return diagnosticsByItemId;
}

function resolvePricingTableDisplayCategory(
  category: string | null | undefined
): {
  key: string;
  label: string;
  sectionId: string;
  sortOrder: number;
} {
  const legacyKey = resolvePricingLegacyCategoryKey(category);
  const key =
    legacyKey ??
    normalizePricingTableDisplayKey(category) ??
    DEFAULT_PRICING_SECTION_KEY;
  const label =
    toPricingLegacyCategoryLabel(category) ?? DEFAULT_PRICING_SECTION_LABEL;
  const budgetCategory = mapPricingContentCategoryToBudgetCategory(category);

  return {
    key,
    label,
    sectionId: `pricing-category:${key}`,
    sortOrder: PRICING_SECTION_ORDER_BY_BUDGET_CATEGORY[budgetCategory],
  };
}

function resolvePricingTableDisplaySubcategory(
  subcategory: string | null | undefined
): {
  key: string | null;
  label: string | null;
} {
  if (!subcategory) {
    return { key: null, label: null };
  }

  return {
    key:
      resolvePricingLegacySubcategoryKey(subcategory) ??
      normalizePricingTableDisplayKey(subcategory),
    label: toPricingLegacySubcategoryLabel(subcategory),
  };
}

function normalizePricingTableDisplayKey(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  const normalized = value
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

  return normalized.length > 0 ? normalized : null;
}

function resolveItemDepth(
  item: NormalizedItemInput,
  itemsById: ReadonlyMap<string, NormalizedItemInput>
): number {
  let depth = 0;
  let parentId = item.parentId;
  const visited = new Set<string>([item.id]);

  while (parentId) {
    depth += 1;
    if (visited.has(parentId)) {
      break;
    }
    visited.add(parentId);
    const parent = itemsById.get(parentId);
    if (!parent) {
      break;
    }
    parentId = parent.parentId;
  }

  return depth;
}

function buildPersistenceSafeFields(
  input: PricingInput,
  itemTotals: ItemTotal[],
  versionPrice: PricingTableTotals
): PersistenceSafeCalculatedFields {
  return {
    baseCurrency: input.context.baseCurrency,
    versionPrice:
      versionPrice.finalPrice === null
        ? null
        : moneyAmount(versionPrice.finalPrice, input.context.baseCurrency),
    versionPriceWithTax:
      versionPrice.finalPriceWithTax === null
        ? null
        : moneyAmount(
            versionPrice.finalPriceWithTax,
            input.context.baseCurrency
          ),
    items: itemTotals.map(
      (total): PersistenceSafeCalculatedItem => ({
        itemId: total.item.id,
        sourceId: total.item.sourceId ?? null,
        priceStructureKind: total.item.priceStructure.kind,
        inclusion: total.inclusion,
        money: total.money,
        structure: total.structure,
        lineItem: {
          isLine: total.item.flags.isLine,
          isManualLine: total.item.flags.isManualLine,
          startTime: total.item.startTime ?? null,
          parentId: total.item.parentId,
          rootItemId: total.item.rootItemId ?? null,
        },
        roomIds: total.roomIds,
        travellerIds: total.travellerIds,
        thresholdIds: total.thresholdIds?.filter(Boolean),
      })
    ),
    exchangeRateSnapshot: input.exchangeRates.rates.map((rate) => ({
      sourceCurrency: rate.sourceCurrency,
      targetCurrency: rate.targetCurrency,
      baseCurrency: rate.baseCurrency,
      rate: rate.rate,
      source: rate.source,
      locked: rate.locked,
      sourceRef: rate.sourceRef,
    })),
  };
}

function getItemUnit(structure: NormalizedPriceStructure) {
  switch (structure.kind) {
    case 'single':
      return structure.unit;
    case 'rooms':
      return structure.unit;
    case 'travellers':
      return structure.unit;
    case 'group':
      return structure.unit;
    default:
      return 'SINGLE';
  }
}

function totalForScope(
  itemTotals: ItemTotal[],
  scope: keyof InclusionPolicyResult,
  input: PricingInput
): PricingTableTotals {
  return buildTotals(
    itemTotals.filter((total) => total.inclusion[scope]),
    input.context.baseCurrency
  );
}

function buildTotals(
  itemTotals: ItemTotal[],
  currency: CurrencyCode
): PricingTableTotals {
  const totals = itemTotals.reduce<TotalsAccumulator>(
    (acc, item) => ({
      cost: add(acc.cost, item.finalCost),
      netCost: add(acc.netCost, item.money.netCost ?? 0),
      finalPrice: add(acc.finalPrice, item.money.finalPrice ?? 0),
      finalPriceWithTax: add(
        acc.finalPriceWithTax,
        item.money.finalPriceWithTax ?? 0
      ),
      profit: add(acc.profit, item.money.profit ?? 0),
      commission: add(acc.commission, item.finalCommissionAmount),
    }),
    {
      cost: 0,
      netCost: 0,
      finalPrice: 0,
      finalPriceWithTax: 0,
      profit: 0,
      commission: 0,
    }
  );
  const payedCost = subtract(totals.finalPrice, totals.profit);
  const markup =
    totals.finalPrice !== 0 && payedCost !== 0
      ? multiply(subtract(divide(totals.finalPrice, payedCost), 1), 100)
      : 0;

  return {
    currency,
    cost: smartRoundToTwo(totals.cost),
    netCost: smartRoundToTwo(totals.netCost),
    finalPrice: smartRoundToTwo(totals.finalPrice),
    finalPriceWithTax: smartRoundToTwo(totals.finalPriceWithTax),
    profit: smartRoundToTwo(totals.profit),
    commission: smartRoundToTwo(totals.commission),
    markup,
  };
}

// ── Surcharge & Agency Commission ────────────────────────────────────────────
// Stage 2-4 of the documented pipeline: aggregate the category bases, evaluate
// every Surcharge line, then evaluate every Agency Commission line (the
// Services formula consumes the resolved surcharge Price). Order is
// load-bearing — see `apps/itineraries/prd/pricing-surcharge-agency-commission.md`.
//
// Both blocks gross up (`base / (1 - rate)`) through ONE rounding rule since
// XT-563 (`grossUpAdjustmentAmount`), and they differ on the cost side: a
// Surcharge is margin (`totalCost: 0`), a commission is a cost we pay. Read the
// two `evaluate*Line` functions together.

function buildPricingAdjustments(
  input: PricingInput,
  itemTotals: ItemTotal[],
  diagnostics: PricingDiagnostic[]
): PricingAdjustmentsOutput {
  const output = evaluatePricingAdjustments(input, itemTotals, diagnostics);
  const optionalDistribution = buildOptionalAdjustmentDistribution(
    input,
    itemTotals
  );
  return Object.keys(optionalDistribution).length > 0
    ? { ...output, optionalDistribution }
    : output;
}

function evaluatePricingAdjustments(
  input: PricingInput,
  itemTotals: ItemTotal[],
  diagnostics: PricingDiagnostic[]
): PricingAdjustmentsOutput {
  const adjustments: PricingAdjustmentsInput = input.pricingAdjustments ?? {
    surcharges: [],
    agencyCommissions: [],
  };
  const { bases, hasServiceRow, hasAccommodationRow } =
    buildPricingAdjustmentBases(itemTotals);

  // XT-1014: the two blocks no longer collect their money the same way.
  //
  // The Surcharge and every commission that is NOT scoped to Accommodation are
  // COLLECTED from the service rows (XT-531) — the fee rides along on the
  // services, because a service is not a figure the customer can look up
  // online. An Accommodation-scoped commission is CARRIED by the hotel rows
  // themselves (each hotel's share in `distribution`), so it prices on a
  // hotels-only itinerary too.
  //
  // So the gate is scope-aware: the blocks apply when there is a service row to
  // collect from, OR when there is an Accommodation row to carry an
  // Accommodation-scoped commission. With neither there is nowhere for the
  // money to go, so the blocks are inert rather than silently inflating a
  // total the quote cannot reproduce: empty lines, zero totals, and (via
  // `isApplicable`) no panels on screen.
  //
  // Evaluated BEFORE the lines so an out-of-range percent on an inert block
  // raises no diagnostic either — there is no amount to guard.
  const carriesAccommodationCommission =
    hasAccommodationRow &&
    adjustments.agencyCommissions.some(
      (line) => line.scope === 'ACCOMMODATION'
    );

  if (!hasServiceRow && !carriesAccommodationCommission) {
    return {
      bases,
      distribution: {},
      isApplicable: false,
      surcharge: { lines: [], totals: { totalCost: 0, price: 0 } },
      agencyCommission: { lines: [], totals: { totalCost: 0, price: 0 } },
    };
  }

  // The service-collected money needs a service row to land on. Without one it
  // stays inert even though the Accommodation commission prices — a fee
  // nothing carries is money the quote cannot reproduce.
  const surchargeLines = hasServiceRow
    ? adjustments.surcharges.map((line) =>
        evaluateSurchargeLine(line, bases, diagnostics)
      )
    : [];
  const surchargeTotals = sumAdjustmentLines(surchargeLines);

  const commissionLines = adjustments.agencyCommissions
    .filter((line) => hasServiceRow || line.scope === 'ACCOMMODATION')
    .map((line) =>
      evaluateAgencyCommissionLine(
        line,
        bases,
        surchargeTotals.price,
        diagnostics
      )
    );

  // One block-agnostic pass over both lists, so neither block can drift from the
  // rule and a line is flagged with the SAME wording wherever it lives.
  for (const line of [...surchargeLines, ...commissionLines]) {
    reportStalePriceOverride(line, diagnostics);
  }

  const commissionTotals = sumAdjustmentLines(commissionLines);

  return {
    bases,
    distribution: buildAdjustmentDistribution(
      itemTotals,
      surchargeTotals,
      commissionLines
    ),
    isApplicable: true,
    surcharge: { lines: surchargeLines, totals: surchargeTotals },
    agencyCommission: {
      lines: commissionLines,
      totals: commissionTotals,
    },
  };
}

// Relocate the two blocks' money onto the items that carry it.
//
// Why the money moves at all: an Accommodation line has to print at the rate the
// customer can look up online ("to not overpass online rates"), so no fee may
// ever land on one. The Surcharge and the Services-scoped commissions are
// therefore COLLECTED from the services: their combined total is spread
// pro-rata over the non-Accommodation top-level items (XT-531). The itinerary
// total does not move — the money is only relocated out of two dedicated lines
// and into the services.
//
// An Accommodation-scoped commission is the exception (XT-1014): it is the
// agency's margin on the hotel, so it is CARRIED by the hotels themselves —
// spread pro-rata over the top-level Accommodation items by their price incl
// VAT, never onto a service. The pricing table still prints the hotels at their
// own price and the commission as its own line; every surface that reads this
// map (Excel quote, Customer Preview, agent-portal midlayer) shows each hotel
// with its share folded in.
//
// Distribution unit is the TOP-LEVEL ITEM: a subitem's price counts toward its
// parent's weight and the parent carries the combined share. Product's worked
// example:
//
//   parent 1  100                    weight 100
//   parent 2   30 + sub 10 + sub 10  weight  50
//                             total weight 150
//   fees 75 -> parent 1 gets 50, parent 2 gets 25
//
// Anchoring and the Accommodation test run over the SAME `pricingTableTotal`
// pass that produced `bases`, so the weights and the base can never disagree
// about what counts as a service (XT-531, generalised in XT-596).
function buildAdjustmentDistribution(
  itemTotals: ItemTotal[],
  surchargeTotals: PricingAdjustmentBlockTotals,
  commissionLines: readonly PricingAgencyCommissionLineOutput[]
): PricingAdjustmentDistribution {
  const totalsById = new Map(itemTotals.map((total) => [total.item.id, total]));
  const distribution: PricingAdjustmentDistribution = {};

  const sumLines = (
    lines: readonly PricingAgencyCommissionLineOutput[],
    field: 'price' | 'totalCost'
  ): number => lines.reduce((sum, line) => add(sum, line[field]), 0);
  const accommodationLines = commissionLines.filter(
    (line) => line.scope === 'ACCOMMODATION'
  );
  const serviceCommissionLines = commissionLines.filter(
    (line) => line.scope !== 'ACCOMMODATION'
  );

  // The two pools land on disjoint anchors — services vs hotels — so neither
  // can overwrite the other's share.
  spreadOverAnchors(
    distribution,
    feeCarryingAnchors(itemTotals, totalsById, 'services'),
    roundMoneyAmount(
      surchargeTotals.price + sumLines(serviceCommissionLines, 'price')
    ),
    roundMoneyAmount(
      surchargeTotals.totalCost + sumLines(serviceCommissionLines, 'totalCost')
    )
  );
  spreadOverAnchors(
    distribution,
    feeCarryingAnchors(itemTotals, totalsById, 'accommodation'),
    roundMoneyAmount(sumLines(accommodationLines, 'price')),
    roundMoneyAmount(sumLines(accommodationLines, 'totalCost'))
  );
  return distribution;
}

// The share an OPTIONAL item would carry were it accepted (XT-969). An
// optional service sits outside the total, so `distribution` gives it nothing
// and its card would print its bare price — then jump by its fee the moment
// the agent accepts it. So each optional top-level item is priced as accepted:
// the blocks are evaluated again with that one item in the total, and it takes
// the share it gets there. That is exact whatever else is on the trip — also
// when it is the only service, where accepting it is what makes the
// service-collected blocks apply at all — and for fixed or overridden lines.
//
// Display only: no total, budget or export reads this map, so the itinerary's
// money never moves. Only an item left out of the total for being optional
// qualifies: a hidden, deleted or excluded one would stay out even when
// accepted.
function buildOptionalAdjustmentDistribution(
  input: PricingInput,
  itemTotals: ItemTotal[]
): PricingAdjustmentDistribution {
  const totalsById = new Map(itemTotals.map((total) => [total.item.id, total]));
  const outOnlyForBeingOptional = (total: ItemTotal) =>
    !total.inclusion.pricingTableTotal &&
    total.item.flags.isOptional &&
    isIncluded(
      { ...total.item.flags, isOptional: false },
      input.inclusionPolicies.pricingTableTotal
    );
  const anchorOf = (total: ItemTotal) =>
    resolveSectionAnchorTotal(total, totalsById).item.id;

  const distribution: PricingAdjustmentDistribution = {};
  const anchorIds = new Set(
    itemTotals
      .filter(
        (total) =>
          outOnlyForBeingOptional(total) &&
          // The card that carries a share is the top-level one; an optional
          // subitem of an included parent moves that parent's share instead.
          outOnlyForBeingOptional(resolveSectionAnchorTotal(total, totalsById))
      )
      .map(anchorOf)
  );
  for (const anchorId of anchorIds) {
    const accepted = itemTotals.map((total) =>
      outOnlyForBeingOptional(total) && anchorOf(total) === anchorId
        ? {
            ...total,
            inclusion: { ...total.inclusion, pricingTableTotal: true },
          }
        : total
    );
    // Its own diagnostics are the accepted trip's, not this one's: dropped.
    const share = evaluatePricingAdjustments(input, accepted, []).distribution[
      anchorId
    ];
    if (share && (share.price !== 0 || share.cost !== 0)) {
      distribution[anchorId] = share;
    }
  }
  return distribution;
}

function spreadOverAnchors(
  distribution: PricingAdjustmentDistribution,
  anchors: { id: string; weight: number }[],
  price: number,
  cost: number
): void {
  if ((price === 0 && cost === 0) || anchors.length === 0) return;
  const weights = anchors.map((anchor) => anchor.weight);
  const priceShares = allocateByWeight(price, weights);
  const costShares = allocateByWeight(cost, weights);
  anchors.forEach((anchor, index) => {
    distribution[anchor.id] = {
      price: priceShares[index] ?? 0,
      cost: costShares[index] ?? 0,
    };
  });
}

// The top-level items that may carry a fee, with their weights.
//
// A €0 item is NOT a fee carrier. It weighs nothing, so pro-rata already gives
// it nothing — except in the one case where every service is free, where the
// allocator's "no weight at all" fallback would split evenly and print a fee on
// a line the quote shows as 0,00. A free suggestion (a restaurant the traveller
// pays directly) must stay at 0,00, so the priced items carry the whole amount
// and, when none exists, the money stays where the consultant can still see it
// rather than materialising on a free line.
//
// One bucket per top-level item, insertion-ordered so the largest-remainder
// tie-break is stable across runs.
//
// `services` weighs the non-Accommodation items by their Price; `accommodation`
// weighs the hotels by their Price INCL VAT — the same figure the
// Accommodation commission's base sums (XT-1014), so each hotel carries
// `price_i / (1 - rate) - price_i` up to the rounding of the shares.
function feeCarryingAnchors(
  itemTotals: ItemTotal[],
  totalsById: ReadonlyMap<string, ItemTotal>,
  pool: 'services' | 'accommodation'
): { id: string; weight: number }[] {
  const weightByAnchorId = new Map<string, number>();

  for (const total of itemTotals) {
    if (!total.inclusion.pricingTableTotal) continue;
    const anchor = resolveSectionAnchorTotal(total, totalsById);
    const isHotel = isPricingAccommodationCategory(anchor.item.contentCategory);
    if (isHotel !== (pool === 'accommodation')) continue;
    weightByAnchorId.set(
      anchor.item.id,
      add(
        weightByAnchorId.get(anchor.item.id) ?? 0,
        safeNumber(
          isHotel ? total.money.finalPriceWithTax : total.money.finalPrice
        )
      )
    );
  }

  return [...weightByAnchorId.entries()]
    .filter(([, weight]) => safeNumber(weight) > 0)
    .map(([id, weight]) => ({ id, weight }));
}

// The bases are the same aggregates the pricing table prints as its section
// Price totals — the Accommodation section total and the sum of every other
// section — so the blocks always reconcile against what the consultant sees.
// `services` is derived by EXCLUDING accommodation, never by listing the
// service categories, so a new content category is included automatically.
// A subitem contributes to its parent's category (`resolveSectionAnchorTotal`),
// matching how the sections themselves are grouped.
//
// Also publishes `hasServiceRow` and `hasAccommodationRow` — whether a row of
// that kind EXISTS, which is a different question from whether the base is
// non-zero: a free (€0) service is still somewhere the export can put the fees,
// while a €0 `services` base could equally mean "no service rows at all". The
// two are derived in the same pass so they can never disagree about what counts
// as Accommodation (XT-531).
//
// `hasAccommodationRow` is what keeps an Accommodation-scoped commission
// applicable on a hotels-only itinerary (XT-1014): it is carried by the hotel
// rows themselves, so it needs a hotel to land on — not a service to collect
// from.
function buildPricingAdjustmentBases(itemTotals: ItemTotal[]): {
  bases: PricingAdjustmentBases;
  hasServiceRow: boolean;
  hasAccommodationRow: boolean;
} {
  const totalsById = new Map(itemTotals.map((total) => [total.item.id, total]));
  let accommodation = 0;
  let accommodationWithVat = 0;
  let services = 0;
  let hasServiceRow = false;
  let hasAccommodationRow = false;
  // XT-964 — the same services, split by the anchor's commission category.
  const byCategory = {
    experiences: 0,
    dining: 0,
    transportation: 0,
    otherServices: 0,
  };

  for (const total of itemTotals) {
    if (!total.inclusion.pricingTableTotal) continue;
    const anchor = resolveSectionAnchorTotal(total, totalsById);
    const price = safeNumber(total.money.finalPrice);
    if (isPricingAccommodationCategory(anchor.item.contentCategory)) {
      accommodation = add(accommodation, price);
      // XT-1014: the Accommodation commission is the agency's margin on the
      // hotel as the CUSTOMER pays for it — the accommodation total INCL VAT.
      // Every other scope keeps its ex-VAT base (pinned by earlier tickets).
      accommodationWithVat = add(
        accommodationWithVat,
        safeNumber(total.money.finalPriceWithTax)
      );
      hasAccommodationRow = true;
    } else {
      services = add(services, price);
      hasServiceRow = true;
      const key = serviceCategoryBaseKey(anchor.item.contentCategory);
      byCategory[key] = add(byCategory[key], price);
    }
  }

  const accommodationBase = smartRoundToTwo(accommodation);
  const servicesBase = smartRoundToTwo(services);

  return {
    bases: {
      accommodation: accommodationBase,
      accommodationWithVat: smartRoundToTwo(accommodationWithVat),
      services: servicesBase,
      // Spec definition: the sum of the two published bases (not an independent
      // re-aggregation), so "All Categories" always equals what the two scoped
      // figures add up to.
      allCategories: roundMoneyAmount(add(accommodationBase, servicesBase)),
      experiences: smartRoundToTwo(byCategory.experiences),
      dining: smartRoundToTwo(byCategory.dining),
      transportation: smartRoundToTwo(byCategory.transportation),
      otherServices: smartRoundToTwo(byCategory.otherServices),
    },
    hasServiceRow,
    hasAccommodationRow,
  };
}

type ServiceCategoryBaseKey =
  | 'experiences'
  | 'dining'
  | 'transportation'
  | 'otherServices';

const serviceCategoryBaseKey = (
  contentCategory: string | null | undefined
): ServiceCategoryBaseKey => {
  switch (commissionCategoryOfContent(contentCategory)) {
    case 'Experiences':
      return 'experiences';
    case 'Dining':
      return 'dining';
    case 'Transportation':
      return 'transportation';
    default:
      return 'otherServices';
  }
};

const SERVICE_CATEGORY_BASE_BY_SCOPE: Record<
  (typeof PRICING_COMMISSION_SERVICE_CATEGORY_SCOPES)[number],
  ServiceCategoryBaseKey
> = {
  EXPERIENCES: 'experiences',
  DINING: 'dining',
  TRANSPORTATION: 'transportation',
  OTHER_SERVICES: 'otherServices',
};

// A category-scoped Services commission's base: the category's services plus
// its pro-rata share of the surcharge, so lines at one rate add up to the
// single Services line.
const serviceCategoryCommissionBase = (
  scope: (typeof PRICING_COMMISSION_SERVICE_CATEGORY_SCOPES)[number],
  bases: PricingAdjustmentBases,
  surchargePrice: number
): number => {
  const categoryBase = bases[SERVICE_CATEGORY_BASE_BY_SCOPE[scope]];
  if (bases.services <= 0 || categoryBase <= 0) return 0;
  const share = (surchargePrice * categoryBase) / bases.services;
  return roundMoneyAmount(add(categoryBase, share));
};

function evaluateSurchargeLine(
  line: PricingSurchargeLineInput,
  bases: PricingAdjustmentBases,
  diagnostics: PricingDiagnostic[]
): PricingSurchargeLineOutput {
  const base =
    line.scope === 'ALL_CATEGORIES' ? bases.allCategories : bases.services;
  const rate = resolveAdjustmentRate(line.percent, line.id, diagnostics);
  const formulaAmount = grossUpAdjustmentAmount(base, rate);

  return {
    id: line.id,
    kind: 'surcharge',
    scope: line.scope,
    percent: line.percent,
    base,
    formulaAmount,
    // A Surcharge is OUR MARGIN, not money we pay out, so it contributes
    // nothing to the itinerary cost and its whole Price lands in profit
    // (XT-543). Contrast `evaluateAgencyCommissionLine`, which does pay out.
    totalCost: 0,
    ...resolveAdjustmentPrice(formulaAmount, line.priceOverride),
  };
}

function evaluateAgencyCommissionLine(
  line: PricingAgencyCommissionLineInput,
  bases: PricingAdjustmentBases,
  surchargePrice: number,
  diagnostics: PricingDiagnostic[]
): PricingAgencyCommissionLineOutput {
  const rate = resolveAdjustmentRate(line.percent, line.id, diagnostics);
  // The base is the scope's aggregate; the FORMULA is the same for every scope
  // (XT-1014): the percentage is the agency's margin ON the final selling
  // price, so the amount is a gross-up of the base.
  // Accommodation: the accommodation total INCL VAT (XT-1014) — the agency's
  // margin on the hotel as the CUSTOMER pays for it.
  // Services: the base carries the surcharge's resolved Price (the
  // possibly-overridden value), never its Total Cost. The Accommodation base
  // is not affected by the surcharge.
  const base =
    line.scope === 'ACCOMMODATION'
      ? bases.accommodationWithVat
      : line.scope === 'SERVICES'
        ? roundMoneyAmount(add(bases.services, surchargePrice))
        : serviceCategoryCommissionBase(line.scope, bases, surchargePrice);
  const formulaAmount = grossUpAdjustmentAmount(base, rate);

  return {
    id: line.id,
    kind: 'agencyCommission',
    scope: line.scope,
    percent: line.percent,
    base,
    formulaAmount,
    // We pay the agency, so a commission IS a cost: it moves cost and price by
    // the same amount and its net effect on profit is 0 (XT-543).
    totalCost: formulaAmount,
    ...resolveAdjustmentPrice(formulaAmount, line.priceOverride),
  };
}

// A GROSS-UP, not a plain percentage: the rate is the share the fee takes OF the
// grossed-up figure, so 3% on 1 000 is `1000 / 0,97 - 1000 = 30,92` → 31, not
// `1000 × 3% = 30` (XT-543, ticket comment of 2026-08-07).
//
// ONE helper for EVERY block and scope — the Surcharge, an Accommodation
// Agency Commission and a Services Agency Commission alike. They used to round
// at different points (the surcharge rounded the difference, the commission
// rounded the selling price first), which agreed on a whole-euro base and
// diverged as soon as it carried cents. XT-563 retired that divergence: the
// reporter's case is `4 956,50 @12%`, where rounding the selling price gives
// `round(5 632,3863) = 5 632` → **675,50**, while the fee they quote is
// `round(675,8863)` = **676**. So the WHOLE-EURO figure is the fee, and the
// selling price is what carries the cents.
//
// XT-1014 extended it to the Accommodation scope: an Accommodation commission
// used to be a plain percentage of its base (7,5% on 1 700 = 127,50) and is now
// the same gross-up as every other scope — `1 700 / 0,925 - 1 700 = 137,84` →
// 138 — so a hotel's own total inflates to `price / (1 - rate)`.
//
// Do not "restore" selling-price rounding, and do not split this back into two
// helpers — a test pins that a base of 1 009,40 at 3% yields 31,00 as a Surcharge
// *and* as a Services commission.
//
// Kept on raw arithmetic (`/`, `-`, `Math.round`) rather than the 4-decimal
// `divide`/`subtract` money helpers, which would add a second, invisible
// rounding on top of the deliberate one.
//
// Floored at 0: rounding the difference DOWN can only reach 0, never below it, so
// the floor is belt-and-braces rather than a reachable case — but it is what makes
// "no adjustment line can ever reduce the quote" true of the whole feature. A rate
// of 0 (or a base of 0) must still yield 0, and `rate >= 1` would divide by zero
// or flip the sign.
function grossUpAdjustmentAmount(base: number, rate: number): number {
  if (rate >= 1) return 0;
  return Math.max(0, Math.round(base / (1 - rate) - base));
}

// What an Agency Commission line's formula yields on `base` at `percent`.
// Exported for the agent portal's Mini Planner (XT-964), which estimates the
// agency's commission before a quote exists and must show the figure the
// quote's lines will compute. It is NOT an alternative pricing path: anything
// that has a `PricingInput` in hand must go through `calculatePricing`.
//
// ONE formula for every scope (XT-1014) — the scope picks the BASE, never the
// formula: the rate is the agency's margin ON the final selling price, so the
// amount is the gross-up `base / (1 - rate) - base`.
export function agencyCommissionFormulaAmount(
  base: number,
  percent: number
): number {
  if (!Number.isFinite(base) || base <= 0) return 0;
  if (!isPricingAdjustmentPercentInRange(percent)) return 0;
  return grossUpAdjustmentAmount(base, percentToRatio(percent));
}

// `null` (and a non-finite stored value) means "follow the formula"; any real
// number is a user-fixed Price.
function resolveAdjustmentPrice(
  formulaAmount: number,
  priceOverride: number | null
): { price: number; isPriceOverridden: boolean } {
  if (priceOverride === null || !Number.isFinite(priceOverride)) {
    return { price: formulaAmount, isPriceOverridden: false };
  }
  return { price: roundMoneyAmount(priceOverride), isPriceOverridden: true };
}

// A manual Price override NEVER re-follows the formula (that is its whole point),
// so once a service price moves the two drift apart silently — the consultant is
// then quoting a fee that no longer matches the itinerary it was computed from.
// XT-563: say so. Reported in BOTH directions, not only when the override is the
// lower figure: an override left above the formula overcharges the customer just
// as quietly as one left below it undercharges us.
//
// `warning` severity and `blocksPersistence: false` are load-bearing — the value
// is the consultant's deliberate choice and must never stop a save. The remedy is
// named in the message rather than added as a control: clearing the Price field
// already drops the override (TCP-PR07-021).
function reportStalePriceOverride(
  line: PricingAdjustmentLineOutput,
  diagnostics: PricingDiagnostic[]
): void {
  if (!line.isPriceOverridden || line.price === line.formulaAmount) return;

  const delta = roundMoneyAmount(subtract(line.price, line.formulaAmount));
  const blockLabel = PRICING_ADJUSTMENT_BLOCK_LABELS[line.kind];

  diagnostics.push(
    createDiagnostic({
      category: 'stale_override_warning',
      severity: 'warning',
      code: 'PRICING_ADJUSTMENT_PRICE_OVERRIDE_STALE',
      message: `${blockLabel} line has a manual Price of ${line.price.toFixed(
        2
      )} while its formula now gives ${line.formulaAmount.toFixed(
        2
      )} (${delta > 0 ? '+' : ''}${delta.toFixed(
        2
      )}). Clear the Price field to follow the formula again.`,
      lineId: line.id,
      blocksPersistence: false,
      details: {
        formulaAmount: line.formulaAmount,
        price: line.price,
        delta,
      },
    })
  );
}

// An out-of-range percent yields a 0 amount plus a diagnostic instead of
// dividing by zero (rate = 1) or refunding the agency (negative rate). Kept at
// `warning` severity on purpose: the amount is already safe, so it must not
// block saving the rest of an otherwise-valid pricing draft. The UI blocks the
// out-of-range value at the input instead.
function resolveAdjustmentRate(
  percent: number,
  lineId: string,
  diagnostics: PricingDiagnostic[]
): number {
  if (isPricingAdjustmentPercentInRange(percent)) {
    return percentToRatio(percent);
  }

  diagnostics.push(
    createDiagnostic({
      category: 'hard_validation_error',
      severity: 'warning',
      code: 'PRICING_ADJUSTMENT_PERCENT_OUT_OF_RANGE',
      message: `Pricing adjustment percent ${percent} is out of range (0 <= percent < 100); the line contributes 0.`,
      lineId,
      blocksPersistence: false,
      details: { percent },
    })
  );

  return 0;
}

function sumAdjustmentLines(
  lines: ReadonlyArray<{ totalCost: number; price: number }>
): PricingAdjustmentBlockTotals {
  return lines.reduce<PricingAdjustmentBlockTotals>(
    (accumulator, line) => ({
      totalCost: add(accumulator.totalCost, line.totalCost),
      price: add(accumulator.price, line.price),
    }),
    { totalCost: 0, price: 0 }
  );
}

// Folds both block subtotals into an already-built totals object. The blocks
// carry no VAT and no supplier net cost, so `finalPriceWithTax` moves by the
// same amount as `finalPrice`, while `netCost` and the supplier `commission`
// column stay untouched. Markup is re-derived from the new figures with the
// same formula `buildTotals` uses.
//
// The two blocks land differently on the cost side, and that asymmetry lives in
// the lines' `totalCost` rather than here (XT-543): a Surcharge publishes
// `totalCost: 0`, so this function adds its full Price to profit and nothing to
// cost; an Agency Commission publishes its amount, so cost and price move
// together and profit is unchanged. Keep this function block-agnostic.
function addAdjustmentsToTotals(
  totals: PricingTableTotals,
  adjustments: PricingAdjustmentsOutput
): PricingTableTotals {
  const addedTotalCost = add(
    adjustments.surcharge.totals.totalCost,
    adjustments.agencyCommission.totals.totalCost
  );
  const addedPrice = add(
    adjustments.surcharge.totals.price,
    adjustments.agencyCommission.totals.price
  );

  if (addedTotalCost === 0 && addedPrice === 0) {
    return totals;
  }

  const cost = smartRoundToTwo(add(totals.cost ?? 0, addedTotalCost));
  const finalPrice = smartRoundToTwo(add(totals.finalPrice ?? 0, addedPrice));
  const finalPriceWithTax = smartRoundToTwo(
    add(totals.finalPriceWithTax ?? 0, addedPrice)
  );
  // A price override moves the block's Price without moving its Total Cost;
  // that delta is intentionally reported as profit.
  const profit = smartRoundToTwo(
    add(totals.profit ?? 0, subtract(addedPrice, addedTotalCost))
  );
  const payedCost = subtract(finalPrice, profit);

  return {
    ...totals,
    cost,
    finalPrice,
    finalPriceWithTax,
    profit,
    markup:
      finalPrice !== 0 && payedCost !== 0
        ? multiply(subtract(divide(finalPrice, payedCost), 1), 100)
        : 0,
  };
}

type CollectedBudgetTotals = {
  originalTotals: BudgetCategoryTotalsMutable;
  itemIdsByCategory: Record<BudgetCategory, string[]>;
  auditRows: BudgetMappedItemAuditRow[];
  finalCategoryByItemId: Map<string, BudgetCategory>;
};

function collectBudgetTotals(
  input: PricingInput,
  itemTotals: ItemTotal[],
  diagnostics: PricingDiagnostic[]
): CollectedBudgetTotals {
  const parentIds = new Set(
    input.items
      .map((item) => item.parentId)
      .filter((parentId): parentId is string => Boolean(parentId))
  );
  const originalTotals = createBudgetAccumulator(input.context.baseCurrency);
  const itemIdsByCategory = createBudgetItemIds();
  const auditRows: BudgetMappedItemAuditRow[] = [];
  const finalCategoryByItemId = new Map<string, BudgetCategory>();

  for (const total of itemTotals) {
    const mapping = mapBudgetCategory(
      total.item,
      parentIds,
      total.inclusion,
      diagnostics
    );
    finalCategoryByItemId.set(total.item.id, mapping.finalCategory);
    const amountValue = mapping.includeAmount
      ? safeBudgetAmount(total.money.finalPrice)
      : 0;
    if (amountValue > 0) {
      originalTotals[mapping.finalCategory].amount = add(
        originalTotals[mapping.finalCategory].amount,
        amountValue
      );
      itemIdsByCategory[mapping.finalCategory].push(total.item.id);
    }

    auditRows.push({
      itemId: total.item.id,
      parentId: total.item.parentId,
      sourceCategory: total.item.contentCategory,
      sourceSubcategory: total.item.contentSubcategory,
      originalCategory: mapping.originalCategory,
      mappedCategory: mapping.mappedCategory,
      finalCategory: mapping.finalCategory,
      reasons: mapping.reasons,
      inclusion: total.inclusion,
      amount: moneyAmount(amountValue, input.context.baseCurrency),
      redistributedAmount: null,
    });
  }

  return {
    originalTotals,
    itemIdsByCategory,
    auditRows,
    finalCategoryByItemId,
  };
}

// Each item's share of the Surcharge / Agency Commission lands in that item's
// own category, as it does on its Excel row, so the budget total matches the
// quote. It goes in before the Other redistribution so an Other anchor's share
// follows its price into Activities / Logistic.
function finalizeBudgetOutput(
  collected: CollectedBudgetTotals,
  distribution: PricingAdjustmentDistribution,
  currency: CurrencyCode
): BudgetOutput {
  const { originalTotals, itemIdsByCategory, auditRows } = collected;
  for (const [itemId, share] of Object.entries(distribution)) {
    const category = collected.finalCategoryByItemId.get(itemId);
    if (!category || share.price === 0) continue;
    originalTotals[category].amount = add(
      originalTotals[category].amount,
      share.price
    );
    if (!itemIdsByCategory[category].includes(itemId)) {
      itemIdsByCategory[category].push(itemId);
    }
  }

  const redistributed = cloneBudgetTotals(originalTotals);
  const redistribution = redistributeOther(redistributed, currency);
  applyRedistributionAuditRows(auditRows, redistribution);

  const total = add(
    add(redistributed.accommodation.amount, redistributed.activities.amount),
    add(redistributed.dining.amount, redistributed.logistic.amount)
  );

  return {
    baseCurrency: currency,
    originalCategoryTotals: originalTotals,
    redistribution,
    auditRows,
    categories: {
      accommodation: buildBudgetCategory(
        'accommodation',
        originalTotals,
        redistributed,
        itemIdsByCategory,
        total,
        currency
      ),
      activities: buildBudgetCategory(
        'activities',
        originalTotals,
        redistributed,
        itemIdsByCategory,
        total,
        currency
      ),
      dining: buildBudgetCategory(
        'dining',
        originalTotals,
        redistributed,
        itemIdsByCategory,
        total,
        currency
      ),
      logistic: buildBudgetCategory(
        'logistic',
        originalTotals,
        redistributed,
        itemIdsByCategory,
        total,
        currency
      ),
      other: buildBudgetCategory(
        'other',
        originalTotals,
        redistributed,
        itemIdsByCategory,
        total,
        currency
      ),
    },
    total: moneyAmount(total, currency),
  };
}

function mapBudgetCategory(
  item: NormalizedItemInput,
  parentIds: Set<string>,
  inclusion: InclusionPolicyResult,
  diagnostics: PricingDiagnostic[]
): {
  originalCategory: BudgetCategory;
  mappedCategory: BudgetCategory;
  finalCategory: BudgetCategory;
  reasons: BudgetMappedItemAuditRow['reasons'];
  includeAmount: boolean;
} {
  const originalCategory = categoryFromContent(item.contentCategory);
  const reasons: BudgetMappedItemAuditRow['reasons'] = ['contentCategory'];

  if (!inclusion.budget) {
    if (item.flags.isHidden) reasons.push('hiddenExcluded');
    if (item.flags.isOptional) reasons.push('optionalExcluded');
    return {
      originalCategory,
      mappedCategory: originalCategory,
      finalCategory: originalCategory,
      reasons,
      includeAmount: false,
    };
  }

  if (item.flags.isLine) {
    reasons.push('lineItem');
  }

  if (item.parentId && !item.flags.isLine) {
    return {
      originalCategory,
      mappedCategory: 'logistic',
      finalCategory: 'logistic',
      reasons: [...reasons, 'childItemLogistic'],
      includeAmount: true,
    };
  }

  if (
    originalCategory === 'other' &&
    parentIds.has(item.id) &&
    !item.flags.isLine
  ) {
    return {
      originalCategory,
      mappedCategory: 'other',
      finalCategory: 'other',
      reasons: [...reasons, 'otherRedistribution'],
      includeAmount: false,
    };
  }

  if (originalCategory === 'other' && !item.contentCategory) {
    diagnostics.push(
      createDiagnostic({
        category: 'mapping_warning',
        severity: 'warning',
        code: 'BUDGET_CATEGORY_FALLBACK_OTHER',
        message: 'Missing budget content category fell back to other.',
        itemId: item.id,
        blocksPersistence: false,
      })
    );
    reasons.push('fallbackOther');
  }

  return {
    originalCategory,
    mappedCategory: originalCategory,
    finalCategory: originalCategory,
    reasons,
    includeAmount: true,
  };
}

function categoryFromContent(category: string | null): BudgetCategory {
  return mapPricingContentCategoryToBudgetCategory(category);
}

function redistributeOther(
  totals: BudgetCategoryTotalsMutable,
  currency: CurrencyCode
): BudgetOutput['redistribution'] {
  const other = totals.other.amount;
  const activitiesLogisticSum = add(
    totals.activities.amount,
    totals.logistic.amount
  );
  const redistributedAmounts = createBudgetAccumulator(currency);

  if (other > 0 && activitiesLogisticSum > 0) {
    const ratio = divide(totals.activities.amount, activitiesLogisticSum);
    const activitiesAmount = multiply(other, ratio);
    const logisticAmount = multiply(other, subtract(1, ratio));
    totals.activities.amount = add(totals.activities.amount, activitiesAmount);
    totals.logistic.amount = add(totals.logistic.amount, logisticAmount);
    totals.other.amount = 0;
    redistributedAmounts.activities.amount = activitiesAmount;
    redistributedAmounts.logistic.amount = logisticAmount;
    redistributedAmounts.other.amount = -other;
    return {
      strategy: 'activityLogisticRatio',
      sourceCategory: 'other',
      redistributedAmounts,
      reason: 'Other was redistributed by current activities/logistic ratio.',
    };
  }

  if (other > 0) {
    const split = multiply(other, 0.5);
    totals.activities.amount = add(totals.activities.amount, split);
    totals.logistic.amount = add(totals.logistic.amount, split);
    totals.other.amount = 0;
    redistributedAmounts.activities.amount = split;
    redistributedAmounts.logistic.amount = split;
    redistributedAmounts.other.amount = -other;
    return {
      strategy: 'activityLogisticEqualSplit',
      sourceCategory: 'other',
      redistributedAmounts,
      reason:
        'Other was redistributed equally because activities and logistic were zero.',
    };
  }

  return {
    strategy: 'none',
    sourceCategory: null,
    redistributedAmounts,
    reason: null,
  };
}

function applyRedistributionAuditRows(
  rows: BudgetMappedItemAuditRow[],
  redistribution: BudgetOutput['redistribution']
): void {
  if (redistribution.strategy === 'none') return;
  const otherRows = rows.filter(
    (row) => row.finalCategory === 'other' && row.amount.amount > 0
  );
  const totalOther = sumNumbers(otherRows.map((row) => row.amount.amount));
  if (totalOther === 0) return;
  for (const row of otherRows) {
    const ratio = divide(row.amount.amount, totalOther);
    row.redistributedAmount = moneyAmount(
      multiply(
        add(
          redistribution.redistributedAmounts.activities.amount,
          redistribution.redistributedAmounts.logistic.amount
        ),
        ratio
      ),
      row.amount.currency
    );
    row.reasons = [...row.reasons, 'otherRedistribution'];
  }
}

type BudgetCategoryTotalsMutable = Record<BudgetCategory, MoneyAmount>;

function createBudgetAccumulator(
  currency: CurrencyCode
): BudgetCategoryTotalsMutable {
  return {
    accommodation: moneyAmount(0, currency),
    activities: moneyAmount(0, currency),
    dining: moneyAmount(0, currency),
    logistic: moneyAmount(0, currency),
    other: moneyAmount(0, currency),
  };
}

function createBudgetItemIds(): Record<BudgetCategory, string[]> {
  return {
    accommodation: [],
    activities: [],
    dining: [],
    logistic: [],
    other: [],
  };
}

function cloneBudgetTotals(
  totals: BudgetCategoryTotalsMutable
): BudgetCategoryTotalsMutable {
  return {
    accommodation: { ...totals.accommodation },
    activities: { ...totals.activities },
    dining: { ...totals.dining },
    logistic: { ...totals.logistic },
    other: { ...totals.other },
  };
}

function buildBudgetCategory(
  category: BudgetCategory,
  original: BudgetCategoryTotalsMutable,
  redistributed: BudgetCategoryTotalsMutable,
  itemIds: Record<BudgetCategory, string[]>,
  total: number,
  currency: CurrencyCode
): BudgetOutput['categories'][BudgetCategory] {
  return {
    category,
    total: moneyAmount(redistributed[category].amount, currency),
    originalTotal: moneyAmount(original[category].amount, currency),
    redistributedAmount: moneyAmount(
      subtract(redistributed[category].amount, original[category].amount),
      currency
    ),
    percentage:
      total > 0
        ? multiply(divide(redistributed[category].amount, total), 100)
        : 0,
    itemIds: itemIds[category],
  };
}

function safeBudgetAmount(value: number | null): number {
  return Number.isFinite(value) && value !== null && value >= 0 ? value : 0;
}

function resolveInclusion(
  flags: NormalizedItemFlags,
  policies: PricingInclusionPolicies
): InclusionPolicyResult {
  return {
    pricingTableDisplay: isIncluded(flags, policies.pricingTableDisplay),
    pricingTableTotal: isIncluded(flags, policies.pricingTableTotal),
    versionPrice: isIncluded(flags, policies.versionPrice),
    budget: isIncluded(flags, policies.budget),
    export: isIncluded(flags, policies.export),
  };
}

function isIncluded(
  flags: NormalizedItemFlags,
  policy: PricingInclusionPolicies[keyof PricingInclusionPolicies]
): boolean {
  if (flags.isHidden && policy.hiddenItems === 'exclude') return false;
  if (flags.isOptional && policy.optionalItems === 'exclude') return false;
  if (flags.isDeleted && policy.deletedItems === 'exclude') return false;
  if (flags.isExcluded && policy.excludedItems === 'exclude') return false;
  if (flags.isManualLine && policy.manualLineItems === 'exclude') return false;
  if (flags.isLine && policy.lineItems === 'exclude') return false;
  if (flags.isParent && policy.parentItems === 'exclude') return false;
  if (flags.isChild && policy.childItems === 'exclude') return false;
  if (flags.isSubitem && policy.subitems === 'exclude') return false;

  if (
    policy.parentChildMode === 'includeParentOnly' &&
    (flags.isChild || flags.isSubitem)
  ) {
    return false;
  }
  if (policy.parentChildMode === 'includeChildrenOnly' && flags.isParent) {
    return false;
  }
  if (
    policy.parentChildMode === 'excludeParentAndChildren' &&
    (flags.isParent || flags.isChild || flags.isSubitem)
  ) {
    return false;
  }

  return true;
}

function sumMoney(
  moneyValues: CalculatedMoneyFields[],
  currency: CurrencyCode
): CalculatedMoneyFields {
  if (moneyValues.length === 0) return zeroCalculatedMoney(currency);
  const commonRate = getCommonRate(moneyValues);
  const summedFinalCost = sumNumbers(
    moneyValues.map((money) => money.finalCost ?? 0)
  );
  return {
    currency,
    sourceCurrency: getCommonSourceCurrency(moneyValues),
    ...(commonRate !== undefined ? { rate: commonRate } : {}),
    finalCost: summedFinalCost,
    cost: sumNumbers(moneyValues.map((money) => money.cost ?? 0)),
    netCost: sumNumbers(moneyValues.map((money) => money.netCost ?? 0)),
    finalNetCost: sumNumbers(
      moneyValues.map((money) => money.finalNetCost ?? 0)
    ),
    unitPrice: sumNumbers(moneyValues.map((money) => money.unitPrice ?? 0)),
    finalPrice: sumNumbers(moneyValues.map((money) => money.finalPrice ?? 0)),
    finalPriceWithTax: sumNumbers(
      moneyValues.map((money) => money.finalPriceWithTax ?? 0)
    ),
    profit: sumNumbers(moneyValues.map((money) => money.profit ?? 0)),
    markupPercent: null,
    commissionPercent: null,
    units: sumNumbers(moneyValues.map((money) => money.units ?? 0)),
    costVat: null,
    priceVat: null,
    isGross: moneyValues.some((money) => money.isGross),
    useNetCost: moneyValues.some((money) => money.useNetCost),
  };
}

function getCommonSourceCurrency(
  moneyValues: CalculatedMoneyFields[]
): CurrencyCode | undefined {
  const [first, ...rest] = moneyValues.flatMap((money) =>
    money.sourceCurrency ? [money.sourceCurrency] : []
  );
  if (!first) return undefined;
  return rest.every((sourceCurrency) => sourceCurrency === first)
    ? first
    : undefined;
}

function getCommonRate(
  moneyValues: CalculatedMoneyFields[]
): number | undefined {
  const [first, ...rest] = moneyValues.flatMap((money) =>
    typeof money.rate === 'number' ? [money.rate] : []
  );
  if (first === undefined) return undefined;
  return rest.every((rate) => rate === first) ? first : undefined;
}

function zeroStructureTotal(
  currency: CurrencyCode,
  structure: PersistenceSafeCalculatedStructure
): StructureTotal {
  return {
    money: zeroCalculatedMoney(currency),
    commissionAmount: 0,
    finalCommissionAmount: 0,
    finalCost: 0,
    quantity: 0,
    structure,
  };
}

function zeroCalculatedMoney(currency: CurrencyCode): CalculatedMoneyFields {
  return {
    currency,
    sourceCurrency: currency,
    cost: 0,
    netCost: 0,
    unitPrice: 0,
    finalPrice: 0,
    finalPriceWithTax: 0,
    profit: 0,
    markupPercent: 0,
    commissionPercent: 0,
    units: 0,
    costVat: ZERO_VAT,
    priceVat: ZERO_VAT,
    isGross: false,
    useNetCost: false,
    rate: 1,
    finalCost: 0,
    finalNetCost: 0,
  };
}

function emptyTotals(currency: CurrencyCode): PricingTableTotals {
  return {
    currency,
    cost: 0,
    netCost: 0,
    finalPrice: 0,
    finalPriceWithTax: 0,
    profit: 0,
    commission: 0,
    markup: 0,
  };
}

function createZeroPriceComponent(
  currency: CurrencyCode
): NormalizedPriceComponent {
  return {
    currency,
    cost: 0,
    netCost: 0,
    unitPrice: 0,
    finalPrice: null,
    finalPriceWithTax: null,
    profit: null,
    markupPercent: 0,
    commissionPercent: 0,
    units: 1,
    costVat: ZERO_VAT,
    priceVat: ZERO_VAT,
    isGross: false,
    useNetCost: false,
  };
}

function warnForNegativeMoney(
  item: NormalizedItemInput,
  money: CalculatedMoneyFields,
  diagnostics: PricingDiagnostic[]
): void {
  const values = [money.cost, money.netCost, money.unitPrice, money.finalPrice];
  if (values.some((value) => value !== null && value < 0)) {
    diagnostics.push(
      createDiagnostic({
        category: 'legacy_compatible_warning',
        severity: 'warning',
        code: 'NEGATIVE_PRICING_VALUE',
        message:
          'Negative pricing value was preserved for legacy compatibility.',
        itemId: item.id,
        blocksPersistence: false,
      })
    );
  }
}

function createDiagnostic(params: {
  category: PricingDiagnostic['category'];
  severity: PricingDiagnostic['severity'];
  code: string;
  message: string;
  blocksPersistence: boolean;
  itemId?: string;
  lineId?: string;
  intentId?: string;
  details?: Record<string, unknown>;
}): PricingDiagnostic {
  return params;
}

function moneyAmount(amount: number, currency: CurrencyCode): MoneyAmount {
  return { amount: smartRoundToTwo(amount), currency };
}

function safeNumber(value: number | null | undefined): number {
  return Number.isFinite(value) ? Number(value) : 0;
}

function normalizeZero(value: number): number {
  return value === 0 ? 0 : value;
}

// Exported for the product-edit row adapter (compute-row.ts) so it backs out
// VAT with the engine's own currency.js-precise math. Intentionally NOT
// re-exported from index.ts — calculatePricing stays the only public calc API.
export function excludeVatFromAmount(
  amount: number,
  vat: VatRate | null
): number {
  const percent = vat?.percent ?? 0;
  return divideWithPrecision(amount, add(1, divide(percent, 100)), 2);
}

function includeVatInAmount(amount: number, vat: VatRate | null): number {
  const percent = vat?.percent ?? 0;
  return multiplyWithPrecision(amount, add(1, divide(percent, 100)), 2);
}

// Markup/commission percents are ratios, not money. Deriving them through the
// 4dp money helpers quantized the percent to 2 decimals (490/294 → 66.67%),
// and any later markup-driven recalculation turned a whole entered price into
// 294 × 1.6667 = 490.01. Eight percent decimals round-trip any 2dp price
// against any 2dp cost below ~6M. Decimal-string rounding stays safe at
// magnitudes where scaled integer math (currency.js) would overflow.
const PERCENT_DECIMALS = 8;

// Legacy persisted markup rounded to 2 decimals. A stored markup within half
// that quantum of the price-implied markup is a quantized echo of the price,
// not an independently chosen value (small epsilon absorbs float noise).
const LEGACY_PERCENT_ECHO_TOLERANCE = 0.005 + 1e-9;

function percentage(part: number, total: number): number {
  const raw = (part / (total === 0 ? 1 : total)) * 100;
  return roundPercentValue(raw);
}

// Percent → ratio must not requantize the percent (divide() rounds the ratio
// to 4dp, collapsing 66.66666667% back to 0.6667). Division by exactly 100 is
// float-exact to well past PERCENT_DECIMALS.
function percentToRatio(percent: number): number {
  return percent / 100;
}

// Rounds a monetary amount to 2 decimals with currency.js semantics (the same
// rounding money math already uses). Persistence boundaries use this so legacy
// price JSON carries proper money (490.00), never 4dp artifacts (490.0098).
export function roundMoneyAmount(value: number): number {
  return round(value, 2);
}

// Normalizes a percent to PERCENT_DECIMALS so persisted markup/commission stay
// deterministic JSON numbers (66.66666667) instead of raw float expansions.
export function roundPercentValue(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(PERCENT_DECIMALS));
}

// The selling price a markup percent produces over a cost, at the persistence
// boundary's 2dp. Byte-identical to what a markup-driven `calculatePricing`
// run then persists — `resolveRawUnitPrice`'s markup branch followed by
// `persistMoney` — because it reuses the same `add`/`multiply`/`round`
// helpers rather than re-deriving the arithmetic in float.
//
// Exported for writers that seed a price OUTSIDE a pricing run and must land
// on the value the engine would compute (the org's default category markup
// stamped onto a new itinerary item — `apps/itineraries/api/src/
// itinerary-items/category-markup.ts`). It is NOT an alternative pricing
// path: anything that has a `PricingInput` in hand must go through
// `calculatePricing`.
export function markupPriceFromCost(
  cost: number,
  markupPercent: number
): number {
  if (!Number.isFinite(cost) || !Number.isFinite(markupPercent)) return 0;
  return roundMoneyAmount(
    add(cost, multiply(cost, percentToRatio(markupPercent)))
  );
}

function add(left: number, right: number): number {
  return currency(left, { precision: 4 }).add(right).value;
}

function subtract(left: number, right: number): number {
  return currency(left, { precision: 4 }).subtract(right).value;
}

function multiply(left: number, right: number): number {
  return multiplyWithPrecision(left, right, 4);
}

function divide(left: number, right: number): number {
  return divideWithPrecision(left, right, 4);
}

function multiplyWithPrecision(
  left: number,
  right: number,
  precision: number
): number {
  return currency(left, { precision }).multiply(right).value;
}

function divideWithPrecision(
  left: number,
  right: number,
  precision: number
): number {
  if (right === 0) return 0;
  return currency(left, { precision }).divide(right).value;
}

function sumNumbers(values: number[]): number {
  return values.reduce((sum, value) => add(sum, value), 0);
}

function smartRoundToTwo(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const rounded = round(value, 2);
  const wholeNumber = Math.round(rounded);
  const diff = Math.abs(rounded - wholeNumber);
  if (diff > 0 && diff <= 0.02) return wholeNumber;
  return rounded;
}

function round(value: number, precision: number): number {
  if (!Number.isFinite(value)) return 0;
  return currency(value, { precision }).value;
}
